import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Play, Square, Video,
  Volume2, VolumeX, Download, Trash2, RotateCcw, Sparkles,
  Music, Settings, Mail, Lock, Eye, EyeOff, Mic, 
  Circle, LogOut, Image, Camera, SkipBack, SkipForward, StopCircle, ChevronDown, Maximize, Minimize,
  Sliders, Grid, List, Share2, Upload, Users, Activity, Undo2, Redo2, FileText, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';
import { useAuth } from '@/context/AuthContext';
import ScoreDisplay from '@/components/ScoreDisplay';
import ProFeatures from '@/components/ProFeatures';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_BACKGROUNDS = [
  { id: 'black', name: 'Black', color: '#000000' },
  { id: 'white', name: 'White', color: '#FFFFFF' },
  { id: 'blue', name: 'Blue', color: '#1e3a5f' },
  { id: 'purple', name: 'Purple', color: '#2d1b4e' },
  { id: 'green', name: 'Green', color: '#1a4d2e' },
];

const AI_BACKGROUNDS = [
  { id: 'beach', name: 'Beach Sunset', prompt: 'Beautiful tropical beach at sunset with palm trees, golden hour lighting' },
  { id: 'studio', name: 'Recording Studio', prompt: 'Professional recording studio with colorful LED lights and sound panels' },
  { id: 'city', name: 'City Night', prompt: 'Vibrant city skyline at night with neon lights and skyscrapers' },
  { id: 'forest', name: 'Forest', prompt: 'Peaceful forest scene with sunlight streaming through tall trees' },
  { id: 'concert', name: 'Concert Stage', prompt: 'Live concert stage with dramatic lighting and crowd silhouettes' },
  { id: 'abstract', name: 'Abstract Art', prompt: 'Colorful abstract art background with flowing shapes and gradients' },
];

export default function MobileStudio() {
  return (
    <PageEditProvider slug="mobile-studio">
      <MobileStudioInner />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/mobile-studio" label="Edit Mobile Studio (Dashboard)" />
    </PageEditProvider>
  );
}

function MobileStudioInner() {
  const navigate = useNavigate();
  const { user, login, logout, loading: authLoading, isAuthenticated } = useAuth();
  
  // Auth
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Core
  const [tracks, setTracks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('user');
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [projectName, setProjectName] = useState('Untitled Project');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isUploadingToCloud, setIsUploadingToCloud] = useState(false);
  const [cloudUploadProgress, setCloudUploadProgress] = useState('');
  const [showCloudFiles, setShowCloudFiles] = useState(false);
  const [cloudFiles, setCloudFiles] = useState([]);
  const [cloudFolders, setCloudFolders] = useState([]);
  const [currentCloudFolder, setCurrentCloudFolder] = useState('');
  const [selectedUploadFolder, setSelectedUploadFolder] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isLoadingCloudFiles, setIsLoadingCloudFiles] = useState(false);
  const [isDownloadingFromCloud, setIsDownloadingFromCloud] = useState(false);
  
  // Cloud Storage — proxied server-side via /api/cloud/*, credential never ships to browser
  const authHeaders = () => {
    const t = localStorage.getItem('auth_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  
  // Audio
  const [audioLevel, setAudioLevel] = useState(0);
  
  // AI Background
  const [bgRemovalEnabled, setBgRemovalEnabled] = useState(false);
  const [selectedColorBg, setSelectedColorBg] = useState(COLOR_BACKGROUNDS[0]);
  const [customBgUrl, setCustomBgUrl] = useState(null);
  const [isLoadingBg, setIsLoadingBg] = useState(false);
  const [isGeneratingBg, setIsGeneratingBg] = useState(false);
  
  // Metronome
  const [bpm, setBpm] = useState(120);
  const [metronomeOn, setMetronomeOn] = useState(false);
  
  // Playback Speed (Slow Motion Practice)
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0); // 0.25x to 2x
  
  // Audio Input
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState('default');
  const [mixerMode, setMixerMode] = useState(false); // Disables audio processing for external mixers
  const [inputGain, setInputGain] = useState(100); // Input volume 0-100%
  
  // UI
  const [showSettings, setShowSettings] = useState(false);
  const [showMetronome, setShowMetronome] = useState(false);
  const [showAiBackground, setShowAiBackground] = useState(false);
  const [showEffects, setShowEffects] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [focusedTrackIdx, setFocusedTrackIdx] = useState(null); // null = show all, number = show only that track
  
  // Rhythm Guide
  const [rhythmGuideEnabled, setRhythmGuideEnabled] = useState(false);
  const [beatFlash, setBeatFlash] = useState(false);
  const rhythmIntervalRef = useRef(null);
  
  // Undo/Redo History
  const [undoStack, setUndoStack] = useState([]); // Stack of previous track states
  const [redoStack, setRedoStack] = useState([]); // Stack of undone states
  
  // Score Display
  const [showScoreDisplay, setShowScoreDisplay] = useState(false);
  
  // Pro Features
  const [showProFeatures, setShowProFeatures] = useState(false);
  
  // Share to Gallery
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [shareDescription, setShareDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // Effects state
  const [effects, setEffects] = useState({
    reverb: 0,      // 0-100
    delay: 0,       // 0-100
    bass: 50,       // 0-100 (50 = neutral)
    treble: 50,     // 0-100 (50 = neutral)
    compression: 0  // 0-100
  });
  
  // Refs
  const videoRef = useRef(null);
  const cameraContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const trackRefs = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const metronomeRef = useRef(null);
  const levelRef = useRef(null);
  const segmentRef = useRef(null);
  const frameRef = useRef(null);
  const bgImageRef = useRef(null);
  const audioBuffersRef = useRef({}); // Store decoded audio buffers
  const activeSourcesRef = useRef([]); // Track active audio sources
  const waveformCanvasRefs = useRef({}); // Store waveform canvas refs
  const playheadRef = useRef(null); // Playhead animation frame
  const effectNodesRef = useRef({}); // Store effect nodes for real-time control
  const peakMeterRef = useRef(null); // Peak meter animation frame
  const trackAnalysersRef = useRef({}); // Store analysers for each track
  const inputGainNodeRef = useRef(null); // Input gain control for external audio
  
  // Waveform & playback position state
  const [waveformData, setWaveformData] = useState({}); // { trackId: Float32Array }
  const [playbackPosition, setPlaybackPosition] = useState(0); // 0-1 progress
  const [playbackStartTime, setPlaybackStartTime] = useState(null);
  const [currentTrackDuration, setCurrentTrackDuration] = useState(0);
  const [peakLevels, setPeakLevels] = useState({}); // { trackId: 0-100 }

  // ===== INDEXEDDB AUTO-SAVE =====
  const DB_NAME = 'MobileStudioDB';
  const DB_VERSION = 1;
  const STORE_NAME = 'projects';
  
  // Open IndexedDB
  const openDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  };
  
  // Save project to IndexedDB
  const saveProject = async () => {
    if (tracks.length === 0) return;
    
    setIsSaving(true);
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      
      // Convert blobs to array buffers for storage
      const tracksToSave = await Promise.all(tracks.map(async (track) => {
        const arrayBuffer = await track.blob.arrayBuffer();
        return {
          ...track,
          blobData: arrayBuffer,
          blobType: track.blob.type,
          blob: undefined, // Don't store the blob object
          url: undefined,  // URLs will be recreated on load
        };
      }));
      
      const project = {
        id: 'current_project', // Single project for now
        name: projectName,
        tracks: tracksToSave,
        savedAt: new Date().toISOString(),
        bpm: bpm,
        effects: effects,
      };
      
      await new Promise((resolve, reject) => {
        const request = store.put(project);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
      
      setLastSaved(new Date());
      console.log('Project auto-saved');
    } catch (err) {
      console.error('Failed to save project:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Load project from IndexedDB
  const loadProject = async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      
      const project = await new Promise((resolve, reject) => {
        const request = store.get('current_project');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      if (project && project.tracks && project.tracks.length > 0) {
        // Recreate blobs and URLs from stored data
        const restoredTracks = project.tracks.map((track) => {
          const blob = new Blob([track.blobData], { type: track.blobType });
          const url = URL.createObjectURL(blob);
          return {
            ...track,
            blob,
            url,
            blobData: undefined,
            blobType: undefined,
          };
        });
        
        setTracks(restoredTracks);
        setProjectName(project.name || 'Untitled Project');
        if (project.bpm) setBpm(project.bpm);
        if (project.effects) setEffects(project.effects);
        setLastSaved(new Date(project.savedAt));
        
        toast.success(`Loaded ${restoredTracks.length} track(s) from saved project`);
        return true;
      } else {
        toast.info('No saved project found. Start recording!');
        return false;
      }
    } catch (err) {
      console.error('Failed to load project:', err);
      toast.error('Could not load saved project');
      return false;
    }
  };
  
  // Clear saved project
  const clearSavedProject = async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await new Promise((resolve, reject) => {
        const request = store.delete('current_project');
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
      setLastSaved(null);
      toast.success('Saved project cleared');
    } catch (err) {
      console.error('Failed to clear project:', err);
    }
  };
  
  // Auto-save when tracks change (debounced)
  const autoSaveTimeoutRef = useRef(null);
  useEffect(() => {
    if (tracks.length > 0) {
      // Debounce auto-save by 2 seconds
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveProject();
      }, 2000);
    }
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [tracks, projectName, bpm, effects]);
  
  // Load saved project on mount
  useEffect(() => {
    loadProject();
  }, []);

  // ===== CAMERA =====
  // ===== ENUMERATE AUDIO DEVICES - Same approach as MultiCamStudio =====
  const enumerateAudioDevices = async () => {
    try {
      // Request BOTH video and audio permission first - this is what works in MultiCamStudio
      console.log('Requesting video + audio permission...');
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log('Total devices:', devices.length);
      
      // Audio input devices with type detection (same as MultiCamStudio)
      const audioInputs = devices.filter(d => d.kind === 'audioinput').map((d, i) => {
        const label = (d.label || '').toLowerCase();
        let type = 'built-in';
        let displayName = d.label || `Microphone ${i + 1}`;
        
        // Detect interface type - matches MultiCamStudio logic
        if (label.includes('zoom') || label.includes('podtrak') || label.includes('h6') || label.includes('h8')) {
          type = 'interface';
          displayName = d.label || 'Zoom Recorder';
        } else if (label.includes('x air') || label.includes('xair') || label.includes('xr18') || label.includes('x18')) {
          type = 'interface';
          displayName = d.label || 'X Air 18';
        } else if (label.includes('behringer') || label.includes('midas') || label.includes('scarlett') || 
                   label.includes('focusrite') || label.includes('presonus') || label.includes('apollo') || 
                   label.includes('motu') || label.includes('m-audio') || label.includes('interface')) {
          type = 'interface';
        } else if (label.includes('usb')) {
          type = 'usb';
        } else if (label.includes('bluetooth') || label.includes('airpods') || label.includes('wireless')) {
          type = 'bluetooth';
        } else if (label.includes('headphone') || label.includes('headset') || label.includes('wired')) {
          type = 'headphones';
        }
        
        return {
          deviceId: d.deviceId,
          label: displayName,
          originalLabel: d.label,
          type: type
        };
      });
      
      setAudioInputDevices(audioInputs);
      console.log('Audio inputs found:', audioInputs);
      
      // Show what we found
      if (audioInputs.length > 0) {
        const names = audioInputs.map(d => d.label).join(', ');
        toast.success(`Found ${audioInputs.length} audio input(s): ${names}`);
      }
      
      // Auto-select external device if connected
      const externalDevice = audioInputs.find(d => d.type === 'interface' || d.type === 'usb');
      if (externalDevice) {
        setSelectedAudioInput(externalDevice.deviceId);
        setMixerMode(true);
        toast.info(`Auto-selected: ${externalDevice.label}`);
      }
    } catch (err) {
      console.log('Could not enumerate audio devices:', err);
      toast.error('Could not access audio devices. Please allow microphone permission.');
    }
  };
  
  // Force re-scan of audio devices - useful when plugging in external devices
  const forceRescanDevices = async () => {
    toast.info('Scanning for all audio devices...');
    
    try {
      // Release any existing audio permissions by stopping current stream
      if (stream) {
        stream.getAudioTracks().forEach(t => t.stop());
      }
      
      // Small delay to let iOS recognize device changes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Request fresh permission - this should trigger iOS device picker
      const freshStream = await navigator.mediaDevices.getUserMedia({ 
        audio: true,
        video: true
      });
      freshStream.getTracks().forEach(t => t.stop());
      
      // Now enumerate
      await enumerateAudioDevices();
      
      // Re-init camera with current settings
      if (hasCamera) {
        initCamera();
      }
    } catch (err) {
      console.error('Force rescan error:', err);
      toast.error('Could not scan devices: ' + err.message);
    }
  };
  
  // Test audio input - just audio, no video
  const testAudioInput = async () => {
    try {
      toast.info('Testing audio input...');
      
      // Stop any existing test stream
      if (window.testAudioStream) {
        window.testAudioStream.getTracks().forEach(t => t.stop());
      }
      
      // Request audio only with selected device
      const audioConstraints = {
        deviceId: selectedAudioInput !== 'default' ? { exact: selectedAudioInput } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      };
      
      const testStream = await navigator.mediaDevices.getUserMedia({ 
        audio: audioConstraints, 
        video: false 
      });
      
      window.testAudioStream = testStream;
      
      const audioTrack = testStream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        console.log('Test audio track:', audioTrack.label, settings);
        toast.success(`Audio from: ${audioTrack.label || 'Unknown'}`);
        
        // Create audio context to monitor levels
        const testCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (testCtx.state === 'suspended') await testCtx.resume();
        
        const testAnalyser = testCtx.createAnalyser();
        testAnalyser.fftSize = 256;
        testCtx.createMediaStreamSource(testStream).connect(testAnalyser);
        
        // Monitor for 5 seconds
        const data = new Uint8Array(testAnalyser.frequencyBinCount);
        let maxLevel = 0;
        let checkCount = 0;
        
        const checkLevel = setInterval(() => {
          testAnalyser.getByteFrequencyData(data);
          const level = data.reduce((a, b) => a + b, 0) / data.length;
          if (level > maxLevel) maxLevel = level;
          checkCount++;
          
          if (checkCount >= 50) { // 5 seconds
            clearInterval(checkLevel);
            testStream.getTracks().forEach(t => t.stop());
            testCtx.close();
            
            if (maxLevel > 5) {
              toast.success(`✓ Audio detected! Peak level: ${Math.round(maxLevel)}`);
            } else {
              toast.error('No audio signal detected. Check your device connection.');
            }
          }
        }, 100);
      }
    } catch (err) {
      console.error('Test audio error:', err);
      toast.error(`Audio test failed: ${err.message}`);
    }
  };

  // Switch camera only (preserves audio track)
  const switchCamera = useCallback(async () => {
    if (!stream || isSwitchingCamera) return;
    
    setIsSwitchingCamera(true);
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    
    try {
      // Get the existing audio track to preserve it
      const existingAudioTrack = stream.getAudioTracks()[0];
      
      // Stop only the video track
      stream.getVideoTracks().forEach(t => t.stop());
      
      // Wait for iOS to release the camera
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Request only the new video stream
      let newVideoStream;
      let retries = 3;
      
      while (retries > 0 && !newVideoStream) {
        try {
          newVideoStream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: newFacing,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false  // Don't request audio - we'll keep the existing track
          });
        } catch (err) {
          console.log(`Camera switch attempt failed (${retries} retries left):`, err);
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      if (!newVideoStream) {
        throw new Error('Failed to switch camera after multiple attempts');
      }
      
      // Create a new MediaStream combining old audio with new video
      const newVideoTrack = newVideoStream.getVideoTracks()[0];
      const combinedStream = new MediaStream();
      combinedStream.addTrack(newVideoTrack);
      if (existingAudioTrack) {
        combinedStream.addTrack(existingAudioTrack);
      }
      
      setStream(combinedStream);
      setFacingMode(newFacing);
      setIsSwitchingCamera(false);
      
    } catch (e) {
      console.error('Camera switch error:', e);
      setIsSwitchingCamera(false);
      toast.error('Failed to switch camera. Try again.');
    }
  }, [stream, facingMode, isSwitchingCamera]);

  const initCamera = useCallback(async () => {
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Camera not supported on this browser');
      setHasCamera(false);
      return;
    }
    
    setIsSwitchingCamera(true);
    
    try {
      // Stop existing stream and give iOS time to release the camera hardware
      if (stream) {
        stream.getTracks().forEach(t => {
          t.stop();
          t.enabled = false;
        });
        setStream(null);
        // iOS needs a moment to release the camera before switching
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Build audio constraints based on mixer mode and selected device
      let audioConstraints;
      
      if (mixerMode) {
        // Mixer mode: disable all processing for clean external audio
        // For iOS/Android, we need to be very explicit about disabling processing
        audioConstraints = {
          deviceId: selectedAudioInput !== 'default' ? { exact: selectedAudioInput } : undefined,
          echoCancellation: { exact: false },
          noiseSuppression: { exact: false },
          autoGainControl: { exact: false },
          // Try to get stereo if available
          channelCount: { ideal: 2 },
          // High sample rate for better quality
          sampleRate: { ideal: 48000 },
          sampleSize: { ideal: 16 }
        };
      } else {
        // Normal mode: use device defaults with optional device selection
        audioConstraints = {
          deviceId: selectedAudioInput !== 'default' ? { exact: selectedAudioInput } : undefined,
          echoCancellation: true,
          noiseSuppression: true
        };
      }
      
      // Try with ideal constraints first, fallback to basic if it fails
      let s;
      let audioStream = null;
      
      // For external devices in mixer mode, try getting audio separately first
      if (mixerMode && selectedAudioInput !== 'default') {
        try {
          // Request audio stream separately with strict constraints
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints,
            video: false
          });
          console.log('Got separate audio stream for external device');
          
          // Verify the audio track settings
          const audioTrack = audioStream.getAudioTracks()[0];
          if (audioTrack) {
            // Apply constraints directly to the track for iOS
            try {
              await audioTrack.applyConstraints({
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false
              });
              console.log('Applied constraints directly to audio track');
            } catch (e) {
              console.log('Could not apply track constraints:', e);
            }
          }
          
          // Now get video separately
          const videoStream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: facingMode,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          });
          
          // Combine video and external audio into one stream
          s = new MediaStream([
            ...videoStream.getVideoTracks(),
            ...audioStream.getAudioTracks()
          ]);
          
          toast.success('External audio connected!');
        } catch (e) {
          console.log('Separate audio stream failed, falling back to combined:', e);
          audioStream = null;
        }
      }
      
      // If we didn't get a separate stream, use combined request
      if (!s) {
        // iOS camera switch retry logic
        let retries = 3;
        let lastError = null;
        
        while (retries > 0 && !s) {
          try {
            s = await navigator.mediaDevices.getUserMedia({
              video: { 
                facingMode: facingMode,
                width: { ideal: 1280 },
                height: { ideal: 720 }
              },
              audio: audioConstraints
            });
          } catch (constraintError) {
            console.log(`Camera attempt failed (${retries} retries left):`, constraintError);
            lastError = constraintError;
            
            // For iOS NotReadableError, wait and retry
            if (constraintError.name === 'NotReadableError' || constraintError.name === 'AbortError') {
              retries--;
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 500));
                continue;
              }
            }
            
            // Try basic constraints as last resort
            try {
              s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: facingMode },
                audio: selectedAudioInput !== 'default' ? { deviceId: { exact: selectedAudioInput } } : true
              });
            } catch (basicError) {
              lastError = basicError;
              retries--;
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }
          }
        }
        
        if (!s && lastError) {
          throw lastError;
        }
      }
      
      setStream(s);
      setHasCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        // Ensure video plays on Android
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        videoRef.current.play().catch(e => console.log('Video autoplay:', e));
      }
      
      // Log audio track settings for debugging
      const audioTrack = s.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        const constraints = audioTrack.getConstraints();
        console.log('Audio track label:', audioTrack.label);
        console.log('Audio track settings:', settings);
        console.log('Audio track constraints:', constraints);
        
        // Show user what audio processing is active
        if (mixerMode) {
          const processingActive = settings.echoCancellation || settings.noiseSuppression || settings.autoGainControl;
          if (processingActive) {
            toast.warning('Note: Your device may still be processing audio. Try the "Wide Spectrum" mic mode in iOS Control Center.');
          } else {
            toast.success(`Mixer Mode: Raw audio from ${audioTrack.label || 'External device'}`);
          }
        } else {
          toast.success(`Audio: ${audioTrack.label || 'Default mic'}`);
        }
      }
      
      // Audio meter - wrap in try/catch for Android compatibility
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        // Create input gain node for volume control
        const inputGainNode = ctx.createGain();
        inputGainNode.gain.value = inputGain / 100;
        inputGainNodeRef.current = inputGainNode;
        
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        
        // Connect: source -> inputGain -> analyser
        const source = ctx.createMediaStreamSource(s);
        source.connect(inputGainNode);
        inputGainNode.connect(analyser);
        
        analyserRef.current = analyser;
        const data = new Uint8Array(analyser.frequencyBinCount);
        if (levelRef.current) clearInterval(levelRef.current);
        levelRef.current = setInterval(() => {
          analyser.getByteFrequencyData(data);
          setAudioLevel(Math.min(100, (data.reduce((a, b) => a + b, 0) / data.length) * 1.5));
        }, 50);
      } catch (audioErr) {
        console.log('Audio meter error (non-critical):', audioErr);
      }
      
      setIsSwitchingCamera(false);
      
    } catch (e) {
      console.error('Camera error:', e);
      setIsSwitchingCamera(false);
      
      if (e.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access.');
      } else if (e.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else if (e.name === 'NotReadableError' || e.name === 'AbortError') {
        // iOS specific - camera busy or couldn't switch
        toast.error('Camera busy. Please wait a moment and try again.');
        // Try to recover by waiting and retrying once more
        setTimeout(() => {
          initCamera();
        }, 1000);
        return; // Don't set hasCamera to false yet
      } else if (e.name === 'OverconstrainedError') {
        toast.error('Audio device not available. Try selecting a different input.');
      } else {
        toast.error('Camera error: ' + (e.message || 'Unknown error'));
      }
      setHasCamera(false);
    }
  }, [facingMode, selectedAudioInput, mixerMode]);

  useEffect(() => {
    if (isAuthenticated) {
      // Check for saved audio device from Audio Setup page
      const savedDevice = localStorage.getItem('selectedAudioDevice');
      if (savedDevice) {
        try {
          const device = JSON.parse(savedDevice);
          if (device && device.deviceId) {
            setSelectedAudioInput(device.deviceId);
            setMixerMode(true);
            toast.info(`Using saved device: ${device.label || 'External Audio'}`);
          }
        } catch (e) {
          console.log('Could not parse saved audio device');
        }
      }
      
      enumerateAudioDevices();
      initCamera();
    }
    return () => {
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (levelRef.current) clearInterval(levelRef.current);
      if (metronomeRef.current) clearInterval(metronomeRef.current);
    };
  }, [isAuthenticated]);

  // Re-init camera when audio input or mixer mode changes (NOT facingMode - that's handled by switchCamera)
  useEffect(() => { 
    if (isAuthenticated && hasCamera) initCamera(); 
  }, [selectedAudioInput, mixerMode]);

  // Update input gain in real-time
  useEffect(() => {
    if (inputGainNodeRef.current) {
      inputGainNodeRef.current.gain.value = inputGain / 100;
    }
  }, [inputGain]);

  // Listen for device changes (when plugging in/out external devices)
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('Audio devices changed');
      enumerateAudioDevices();
    };
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Re-attach stream when fullscreen state changes
  useEffect(() => {
    if (stream && videoRef.current) {
      // Ensure the video element has the stream attached after fullscreen toggle
      if (videoRef.current.srcObject !== stream) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(e => console.log('Video play on fullscreen change:', e));
      }
    }
  }, [isFullscreen, stream]);

  // Generate waveforms for new tracks
  useEffect(() => {
    tracks.forEach(async (track) => {
      // Only generate if we don't have waveform data for this track yet
      if (!waveformData[track.id] && track.blob) {
        await decodeTrackAudio(track.id, track.blob);
      }
    });
  }, [tracks]);

  // Toggle fullscreen - CSS-based for mobile compatibility
  const toggleFullscreen = useCallback(() => {
    const wasFullscreen = isFullscreen;
    setIsFullscreen(!isFullscreen);
    
    if (!isFullscreen) {
      toast.success('Fullscreen mode - tap X to exit');
    } else {
      // Exiting fullscreen - need to re-attach stream to video element
      // Use setTimeout to wait for React to render the normal view
      setTimeout(() => {
        if (stream && videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.log('Video resume:', e));
        }
        // Restart frame processing if AI background is on
        if (bgRemovalEnabled && segmentRef.current) {
          processFrame();
        }
      }, 100);
    }
  }, [isFullscreen, stream, bgRemovalEnabled]);

  // ===== AI BACKGROUND =====
  const initBgRemoval = useCallback(async () => {
    if (segmentRef.current) return;
    setIsLoadingBg(true);
    try {
      const seg = new SelfieSegmentation({
        locateFile: f => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747/${f}`
      });
      seg.setOptions({ modelSelection: 1, selfieMode: true });
      seg.onResults(r => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        const w = canvasRef.current.width, h = canvasRef.current.height;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(r.image, 0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(r.segmentationMask, 0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-over';
        if (bgImageRef.current && bgImageRef.current.complete && bgImageRef.current.naturalWidth > 0) {
          ctx.drawImage(bgImageRef.current, 0, 0, w, h);
        } else {
          ctx.fillStyle = selectedColorBg.color;
          ctx.fillRect(0, 0, w, h);
        }
        ctx.globalCompositeOperation = 'source-over';
      });
      await seg.initialize();
      segmentRef.current = seg;
      setIsLoadingBg(false);
      toast.success('AI Background ready');
      processFrame();
    } catch (e) {
      setIsLoadingBg(false);
      setBgRemovalEnabled(false);
    }
  }, [selectedColorBg]);

  const processFrame = () => {
    if (!bgRemovalEnabled || !segmentRef.current || !videoRef.current) {
      frameRef.current = requestAnimationFrame(processFrame);
      return;
    }
    if (videoRef.current.readyState >= 2 && canvasRef.current) {
      canvasRef.current.width = videoRef.current.videoWidth || 640;
      canvasRef.current.height = videoRef.current.videoHeight || 480;
      segmentRef.current.send({ image: videoRef.current }).catch(() => {});
    }
    frameRef.current = requestAnimationFrame(processFrame);
  };

  const generateAiBackground = async (preset) => {
    setIsGeneratingBg(true);
    toast.info(`Generating ${preset.name}...`);
    try {
      const res = await fetch(`${API}/api/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: preset.prompt })
      });
      const data = await res.json();
      if (data.success && data.image_url) {
        const fullUrl = data.image_url.startsWith('http') ? data.image_url : `${API}${data.image_url}`;
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { bgImageRef.current = img; setCustomBgUrl(fullUrl); toast.success(`${preset.name} loaded!`); };
        img.src = fullUrl;
      }
    } catch (e) { toast.error('Generation error'); }
    setIsGeneratingBg(false);
  };

  // ===== METRONOME =====
  const toggleMetronome = () => {
    if (metronomeOn) {
      clearInterval(metronomeRef.current);
      setMetronomeOn(false);
    } else {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      metronomeRef.current = setInterval(() => {
        const osc = audioCtxRef.current.createOscillator();
        const gain = audioCtxRef.current.createGain();
        osc.connect(gain);
        gain.connect(audioCtxRef.current.destination);
        osc.frequency.value = 800;
        gain.gain.setValueAtTime(0.3, audioCtxRef.current.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtxRef.current.currentTime + 0.1);
        osc.start();
        osc.stop(audioCtxRef.current.currentTime + 0.1);
      }, 60000 / bpm);
      setMetronomeOn(true);
    }
  };

  // ===== RECORDING =====
  const startRecording = async () => {
    if (!hasCamera || !stream || tracks.length >= 8) return;
    
    // Play existing tracks for overdub using Web Audio API (more reliable than video elements)
    if (tracks.length > 0) {
      // Ensure audio context is ready
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
      
      // Stop any currently playing sources first
      activeSourcesRef.current.forEach(({ source }) => {
        try { source.stop(); } catch (e) {}
      });
      activeSourcesRef.current = [];
      
      const hasSolo = tracks.some(t => t.solo);
      const startTime = audioCtxRef.current.currentTime + 0.1;
      
      // Play each track using Web Audio API for overdub monitoring
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        const shouldPlay = hasSolo ? track.solo : !track.muted;
        if (!shouldPlay) continue;
        
        try {
          // Decode audio from blob
          const audioBuffer = await decodeTrackAudio(track.id, track.blob);
          if (!audioBuffer) continue;
          
          // Create source and gain
          const source = audioCtxRef.current.createBufferSource();
          const gainNode = audioCtxRef.current.createGain();
          
          source.buffer = audioBuffer;
          gainNode.gain.value = track.volume / 100;
          
          source.connect(gainNode);
          gainNode.connect(audioCtxRef.current.destination);
          
          // Start playback synchronized
          source.start(startTime);
          activeSourcesRef.current.push({ source, gainNode, trackId: track.id });
          
          // Also play video (muted) for visual feedback
          const video = trackRefs.current[i];
          if (video) {
            video.currentTime = 0;
            video.muted = true; // Keep video muted, audio comes from Web Audio
            video.play().catch(() => {});
          }
        } catch (err) {
          console.log('Overdub track play error:', err);
        }
      }
      
      toast.info(`Playing ${tracks.length} track(s) for overdub`);
    }
    
    let recordStream = stream;
    if (bgRemovalEnabled && canvasRef.current) {
      const cs = canvasRef.current.captureStream(30);
      stream.getAudioTracks().forEach(t => cs.addTrack(t));
      recordStream = cs;
    }
    chunksRef.current = [];
    
    // Try different codecs for Android compatibility
    let mimeType = 'video/webm;codecs=vp9,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/mp4';
        }
      }
    }
    console.log('Using mimeType:', mimeType);
    
    let mr;
    try {
      mr = new MediaRecorder(recordStream, { mimeType });
    } catch (e) {
      console.log('MediaRecorder with mimeType failed, trying without:', e);
      mr = new MediaRecorder(recordStream);
    }
    
    mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      // Stop overdub playback
      activeSourcesRef.current.forEach(({ source }) => {
        try { source.stop(); } catch (e) {}
      });
      activeSourcesRef.current = [];
      
      const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
      setTracks(prev => [...prev, {
        id: Date.now(), url: URL.createObjectURL(blob), blob, volume: 100, muted: false, solo: false, name: `Track ${prev.length + 1}`
      }]);
      toast.success(`Track ${tracks.length + 1} recorded!`);
    };
    mr.onerror = (e) => {
      console.error('MediaRecorder error:', e);
      toast.error('Recording failed');
      setIsRecording(false);
    };
    mediaRecorderRef.current = mr;
    mr.start(1000); // Capture in 1 second chunks for better compatibility
    setIsRecording(true);
    toast.info('Recording... Previous tracks playing in background');
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // ===== GENERATE WAVEFORM DATA =====
  const generateWaveform = useCallback((trackId, audioBuffer) => {
    const rawData = audioBuffer.getChannelData(0); // Get first channel
    const samples = 150; // Number of bars in waveform
    const blockSize = Math.floor(rawData.length / samples);
    const waveform = new Float32Array(samples);
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[i * blockSize + j]);
      }
      waveform[i] = sum / blockSize;
    }
    
    // Normalize
    const max = Math.max(...waveform);
    if (max > 0) {
      for (let i = 0; i < waveform.length; i++) {
        waveform[i] = waveform[i] / max;
      }
    }
    
    setWaveformData(prev => ({ ...prev, [trackId]: waveform }));
  }, []);

  // ===== DECODE AUDIO FROM VIDEO BLOB =====
  const decodeTrackAudio = useCallback(async (trackId, blob) => {
    if (audioBuffersRef.current[trackId]) return audioBuffersRef.current[trackId];
    
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Resume audio context if suspended (required on iOS)
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioCtxRef.current.decodeAudioData(arrayBuffer);
      audioBuffersRef.current[trackId] = audioBuffer;
      
      // Generate waveform data for this track
      generateWaveform(trackId, audioBuffer);
      
      return audioBuffer;
    } catch (err) {
      console.log('Failed to decode audio for track', trackId, err);
      console.log('Blob type:', blob.type, 'Size:', blob.size);
      
      // Fallback: Try to extract audio using an audio element
      try {
        console.log('Trying fallback audio extraction...');
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        
        // Wait for audio to load
        await new Promise((resolve, reject) => {
          audio.onloadedmetadata = resolve;
          audio.onerror = reject;
          audio.load();
        });
        
        // Create offline context to extract audio
        const duration = audio.duration;
        const sampleRate = audioCtxRef.current.sampleRate;
        const offlineCtx = new OfflineAudioContext(2, sampleRate * duration, sampleRate);
        
        // Create media element source
        const source = audioCtxRef.current.createMediaElementSource(audio);
        
        // Unfortunately, we can't use this directly with OfflineAudioContext
        // So let's just use the video element playback as fallback
        console.log('Using video element fallback for playback');
        URL.revokeObjectURL(audioUrl);
        
        toast.warning('Using video playback (audio decode failed)');
        return null;
      } catch (fallbackErr) {
        console.log('Fallback also failed:', fallbackErr);
        toast.error('Failed to decode track audio');
        return null;
      }
    }
  }, [generateWaveform]);

  // ===== PLAYHEAD ANIMATION =====
  const startPlayheadAnimation = (duration) => {
    setCurrentTrackDuration(duration);
    setPlaybackStartTime(Date.now());
    setPlaybackPosition(0);
    
    const animate = () => {
      const elapsed = (Date.now() - playbackStartTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      setPlaybackPosition(progress);
      
      if (progress < 1 && isPlaying) {
        playheadRef.current = requestAnimationFrame(animate);
      } else if (progress >= 1) {
        setIsPlaying(false);
        setPlaybackPosition(0);
      }
    };
    
    playheadRef.current = requestAnimationFrame(animate);
  };
  
  const stopPlayheadAnimation = () => {
    if (playheadRef.current) {
      cancelAnimationFrame(playheadRef.current);
      playheadRef.current = null;
    }
    setPlaybackPosition(0);
    setPlaybackStartTime(null);
  };

  // Update playhead animation when playing state changes
  useEffect(() => {
    if (!isPlaying) {
      stopPlayheadAnimation();
      return;
    }
    
    // Animate playhead while playing
    if (playbackStartTime && currentTrackDuration > 0) {
      const animate = () => {
        const elapsed = (Date.now() - playbackStartTime) / 1000;
        const progress = Math.min(elapsed / currentTrackDuration, 1);
        setPlaybackPosition(progress);
        
        if (progress < 1) {
          playheadRef.current = requestAnimationFrame(animate);
        } else {
          setIsPlaying(false);
          setPlaybackPosition(0);
        }
      };
      
      playheadRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (playheadRef.current) {
        cancelAnimationFrame(playheadRef.current);
      }
    };
  }, [isPlaying, playbackStartTime, currentTrackDuration]);

  // Cleanup playhead on unmount
  useEffect(() => {
    return () => {
      if (playheadRef.current) {
        cancelAnimationFrame(playheadRef.current);
      }
    };
  }, []);

  // ===== CREATE EFFECTS CHAIN =====
  const createEffectsChain = (ctx) => {
    // Create nodes
    const inputGain = ctx.createGain();
    const bassFilter = ctx.createBiquadFilter();
    const trebleFilter = ctx.createBiquadFilter();
    const compressor = ctx.createDynamicsCompressor();
    const delayNode = ctx.createDelay(1.0);
    const delayFeedback = ctx.createGain();
    const delayWet = ctx.createGain();
    const outputGain = ctx.createGain();
    
    // Configure bass filter (low shelf)
    bassFilter.type = 'lowshelf';
    bassFilter.frequency.value = 200;
    bassFilter.gain.value = (effects.bass - 50) * 0.3; // -15 to +15 dB
    
    // Configure treble filter (high shelf)
    trebleFilter.type = 'highshelf';
    trebleFilter.frequency.value = 3000;
    trebleFilter.gain.value = (effects.treble - 50) * 0.3; // -15 to +15 dB
    
    // Configure compressor
    compressor.threshold.value = -50 + (100 - effects.compression) * 0.5;
    compressor.knee.value = 40;
    compressor.ratio.value = 1 + (effects.compression / 100) * 11; // 1:1 to 12:1
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    
    // Configure delay
    delayNode.delayTime.value = 0.3; // 300ms delay
    delayFeedback.gain.value = effects.delay / 200; // 0 to 0.5
    delayWet.gain.value = effects.delay / 100; // 0 to 1
    
    // Build chain: input -> bass -> treble -> compressor -> output
    inputGain.connect(bassFilter);
    bassFilter.connect(trebleFilter);
    trebleFilter.connect(compressor);
    compressor.connect(outputGain);
    
    // Delay feedback loop
    compressor.connect(delayNode);
    delayNode.connect(delayFeedback);
    delayFeedback.connect(delayNode);
    delayNode.connect(delayWet);
    delayWet.connect(outputGain);
    
    outputGain.connect(ctx.destination);
    
    // Store refs for real-time updates
    effectNodesRef.current = {
      inputGain,
      bassFilter,
      trebleFilter,
      compressor,
      delayNode,
      delayFeedback,
      delayWet,
      outputGain
    };
    
    return inputGain;
  };

  // Update effects in real-time
  const updateEffects = useCallback(() => {
    const nodes = effectNodesRef.current;
    if (!nodes.bassFilter) return;
    
    nodes.bassFilter.gain.value = (effects.bass - 50) * 0.3;
    nodes.trebleFilter.gain.value = (effects.treble - 50) * 0.3;
    nodes.compressor.threshold.value = -50 + (100 - effects.compression) * 0.5;
    nodes.compressor.ratio.value = 1 + (effects.compression / 100) * 11;
    nodes.delayFeedback.gain.value = effects.delay / 200;
    nodes.delayWet.gain.value = effects.delay / 100;
  }, [effects]);

  // Apply effects when changed during playback
  useEffect(() => {
    if (isPlaying) {
      updateEffects();
    }
  }, [effects, isPlaying, updateEffects]);

  // ===== PLAYBACK - Using Web Audio API for TRUE simultaneous playback =====
  const playAllTracks = async () => {
    if (!tracks.length) return;
    
    // Stop any currently playing sources
    stopAllTracks();
    
    // Ensure audio context is ready
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    
    // Create effects chain
    const effectsInput = createEffectsChain(audioCtxRef.current);
    
    const hasSolo = tracks.some(t => t.solo);
    let playCount = 0;
    const startTime = audioCtxRef.current.currentTime + 0.1; // Small delay for sync
    
    toast.info('Loading tracks...');
    
    // Play each track using Web Audio API
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const shouldPlay = hasSolo ? track.solo : !track.muted;
      
      if (!shouldPlay) continue;
      
      try {
        // Decode audio from blob
        const audioBuffer = await decodeTrackAudio(track.id, track.blob);
        
        if (audioBuffer) {
          // Web Audio playback (preferred)
          const source = audioCtxRef.current.createBufferSource();
          const gainNode = audioCtxRef.current.createGain();
          
          source.buffer = audioBuffer;
          source.playbackRate.value = playbackSpeed; // Apply playback speed
          gainNode.gain.value = track.volume / 100;
          
          // Create analyser for peak metering
          const analyser = audioCtxRef.current.createAnalyser();
          analyser.fftSize = 2048;
          analyser.smoothingTimeConstant = 0.2;
          
          // Connect: source -> analyser -> gainNode -> effects
          source.connect(analyser);
          analyser.connect(gainNode);
          gainNode.connect(effectsInput);
          
          // Store analyser for this track
          trackAnalysersRef.current[track.id] = analyser;
          
          // Start at the same time for sync
          source.start(startTime);
          activeSourcesRef.current.push({ source, gainNode, trackId: track.id, analyser });
          
          // Play video muted (visual only) with matching speed
          const video = trackRefs.current[i];
          if (video) {
            video.muted = true;
            video.currentTime = 0;
            video.playbackRate = playbackSpeed; // Sync video speed
            video.play().catch(() => {});
          }
        } else {
          // Fallback: Use video element for audio (when Web Audio decode fails)
          console.log('Using video element fallback for track', i);
          const video = trackRefs.current[i];
          if (video) {
            video.muted = false; // Unmute - use video's audio
            video.volume = track.volume / 100;
            video.currentTime = 0;
            video.playbackRate = playbackSpeed; // Apply playback speed
            
            if (video.readyState >= 2) {
              video.play().catch(err => console.log('Video fallback play error:', err));
            } else {
              const playWhenReady = () => {
                video.play().catch(err => console.log('Video fallback play error:', err));
                video.removeEventListener('loadeddata', playWhenReady);
              };
              video.addEventListener('loadeddata', playWhenReady);
              video.load();
            }
          }
        }
        
        playCount++;
      } catch (err) {
        console.log('Play error for track', i, err);
        
        // Last resort fallback - try video element
        const video = trackRefs.current[i];
        if (video) {
          video.muted = false;
          video.volume = track.volume / 100;
          video.currentTime = 0;
          video.play().catch(() => {});
          playCount++;
        }
      }
    }
    
    if (playCount > 0) {
      setIsPlaying(true);
      // Start peak metering
      startPeakMetering();
      
      // Start playhead animation - use the longest track duration
      const maxDuration = Math.max(...tracks.filter((_, i) => {
        const t = tracks[i];
        const shouldPlay = hasSolo ? t.solo : !t.muted;
        return shouldPlay && audioBuffersRef.current[t.id];
      }).map(t => audioBuffersRef.current[t.id]?.duration || 0));
      
      if (maxDuration > 0) {
        setCurrentTrackDuration(maxDuration / playbackSpeed); // Adjust duration for speed
        setPlaybackStartTime(Date.now());
      }
      const speedLabel = playbackSpeed === 1 ? '' : ` at ${playbackSpeed}x speed`;
      toast.success(`Playing ${playCount} track${playCount > 1 ? 's' : ''}${speedLabel}`);
    } else {
      toast.error('No tracks to play');
    }
  };

  // ===== PEAK METERING =====
  const startPeakMetering = () => {
    if (peakMeterRef.current) cancelAnimationFrame(peakMeterRef.current);
    
    const updatePeaks = () => {
      const newPeaks = {};
      let hasActiveSources = activeSourcesRef.current.length > 0;
      
      activeSourcesRef.current.forEach(({ trackId, analyser }) => {
        if (analyser) {
          try {
            // Use byte data which is more reliable
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            analyser.getByteTimeDomainData(dataArray);
            
            // Find the peak - byte data is 0-255, with 128 being silence
            let peak = 0;
            for (let i = 0; i < bufferLength; i++) {
              const amplitude = Math.abs(dataArray[i] - 128);
              if (amplitude > peak) {
                peak = amplitude;
              }
            }
            
            // Convert to percentage (128 max deviation = 100%)
            const peakPercent = Math.min(100, (peak / 128) * 100 * 1.2);
            newPeaks[trackId] = peakPercent;
          } catch (e) {
            console.log('Analyser error:', e);
          }
        }
      });
      
      setPeakLevels(newPeaks);
      
      // Continue animation if there are active sources
      if (hasActiveSources) {
        peakMeterRef.current = requestAnimationFrame(updatePeaks);
      }
    };
    
    peakMeterRef.current = requestAnimationFrame(updatePeaks);
  };

  const stopPeakMetering = () => {
    if (peakMeterRef.current) {
      cancelAnimationFrame(peakMeterRef.current);
      peakMeterRef.current = null;
    }
    setPeakLevels({});
    trackAnalysersRef.current = {};
  };

  // ===== RHYTHM GUIDE =====
  const startRhythmGuide = useCallback(() => {
    if (rhythmIntervalRef.current) {
      clearInterval(rhythmIntervalRef.current);
    }
    
    const beatInterval = (60 / bpm) * 1000; // ms per beat
    
    rhythmIntervalRef.current = setInterval(() => {
      setBeatFlash(true);
      setTimeout(() => setBeatFlash(false), 100);
    }, beatInterval);
  }, [bpm]);

  const stopRhythmGuide = useCallback(() => {
    if (rhythmIntervalRef.current) {
      clearInterval(rhythmIntervalRef.current);
      rhythmIntervalRef.current = null;
    }
    setBeatFlash(false);
  }, []);

  // Start/stop rhythm guide based on playback state
  useEffect(() => {
    if (isPlaying && rhythmGuideEnabled) {
      startRhythmGuide();
    } else {
      stopRhythmGuide();
    }
    return () => stopRhythmGuide();
  }, [isPlaying, rhythmGuideEnabled, startRhythmGuide, stopRhythmGuide]);

  // ===== SHARE TO GALLERY =====
  const shareToGallery = async () => {
    if (!user) {
      toast.error('Please log in to share to gallery');
      return;
    }
    
    if (!tracks.length) {
      toast.error('No tracks to share');
      return;
    }
    
    if (!shareTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Get the first track's video data as the main video
      const mainTrack = tracks[0];
      
      // Convert blob to base64
      const reader = new FileReader();
      const videoDataPromise = new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(mainTrack.blob);
      });
      
      const videoData = await videoDataPromise;
      
      // Create form data
      const formData = new FormData();
      formData.append('title', shareTitle);
      formData.append('description', shareDescription);
      formData.append('video_data', videoData);
      formData.append('duration', audioBuffersRef.current[mainTrack.id]?.duration || 0);
      formData.append('track_count', tracks.length);
      formData.append('user_id', user.id);
      formData.append('username', user.name || user.email?.split('@')[0] || 'Anonymous');
      
      const response = await fetch(`${API}/api/studio-gallery/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      toast.success('Shared to gallery successfully!');
      setShowShareModal(false);
      setShareTitle('');
      setShareDescription('');
      
    } catch (err) {
      console.error('Share error:', err);
      toast.error('Failed to share to gallery');
    } finally {
      setIsUploading(false);
    }
  };

  // ===== NATIVE SHARE =====
  const handleNativeShare = async (trackIdx = null) => {
    const track = trackIdx !== null ? tracks[trackIdx] : tracks[0];
    if (!track) {
      toast.error('No track to share');
      return;
    }
    
    if (navigator.share) {
      try {
        const file = new File([track.blob], 'recording.webm', { type: 'video/webm' });
        await navigator.share({
          title: 'My Recording - Escola de Samba Casa Samba Mobile Studio',
          text: 'Check out my recording!',
          files: [file]
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to download
          downloadTrack(track);
        }
      }
    } else {
      // Fallback to download
      downloadTrack(track);
    }
  };

  const downloadTrack = (track) => {
    const link = document.createElement('a');
    link.href = track.url;
    link.download = `${track.name.replace(/\s+/g, '_')}.webm`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const stopAllTracks = () => {
    // Stop Web Audio sources
    activeSourcesRef.current.forEach(({ source }) => {
      try {
        source.stop();
      } catch (e) {
        // Already stopped
      }
    });
    activeSourcesRef.current = [];
    
    // Stop videos
    tracks.forEach((_, i) => { 
      const v = trackRefs.current[i]; 
      if (v) { 
        v.pause(); 
        v.currentTime = 0; 
      } 
    });
    
    // Stop peak metering
    stopPeakMetering();
    
    // Stop playhead animation
    stopPlayheadAnimation();
    setIsPlaying(false);
  };

  const playTrack = async (idx) => {
    const track = tracks[idx];
    if (!track) return;
    
    // Stop all first
    stopAllTracks();
    
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    
    // Create effects chain
    const effectsInput = createEffectsChain(audioCtxRef.current);
    
    try {
      const audioBuffer = await decodeTrackAudio(track.id, track.blob);
      
      if (audioBuffer) {
        // Web Audio playback (preferred)
        const source = audioCtxRef.current.createBufferSource();
        const gainNode = audioCtxRef.current.createGain();
        const analyser = audioCtxRef.current.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.2;
        
        source.buffer = audioBuffer;
        source.playbackRate.value = playbackSpeed; // Apply playback speed
        gainNode.gain.value = track.volume / 100;
        
        source.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(effectsInput);
        
        trackAnalysersRef.current[track.id] = analyser;
        source.start();
        activeSourcesRef.current.push({ source, gainNode, trackId: track.id, analyser });
        
        // Play video muted (visual only) with matching speed
        const v = trackRefs.current[idx];
        if (v) { 
          v.muted = true;
          v.currentTime = 0;
          v.playbackRate = playbackSpeed; // Sync video speed
          v.play().catch(() => {});
        }
        
        setIsPlaying(true);
        startPeakMetering();
        setCurrentTrackDuration(audioBuffer.duration / playbackSpeed); // Adjust for speed
        setPlaybackStartTime(Date.now());
      } else {
        // Fallback: Use video element for audio (when decode fails)
        console.log('Using video element fallback for single track');
        const v = trackRefs.current[idx];
        if (v) {
          v.muted = false; // Unmute - use video's audio
          v.volume = track.volume / 100;
          v.currentTime = 0;
          v.playbackRate = playbackSpeed; // Apply playback speed
          
          // Get duration from video
          const playAndSetup = () => {
            v.play().then(() => {
              setIsPlaying(true);
              setCurrentTrackDuration((v.duration || 30) / playbackSpeed);
              setPlaybackStartTime(Date.now());
              const speedLabel = playbackSpeed === 1 ? '' : ` at ${playbackSpeed}x`;
              toast.success(`Playing track${speedLabel} (video fallback)`);
            }).catch(err => {
              console.log('Video fallback play error:', err);
              toast.error('Playback failed - tap video to play manually');
            });
          };
          
          if (v.readyState >= 2) {
            playAndSetup();
          } else {
            v.addEventListener('loadeddata', playAndSetup, { once: true });
            v.load();
          }
        } else {
          toast.error('Could not play track');
        }
      }
    } catch (err) {
      console.log('Play error:', err);
      
      // Last resort - try video element
      const v = trackRefs.current[idx];
      if (v) {
        v.muted = false;
        v.volume = track.volume / 100;
        v.currentTime = 0;
        v.playbackRate = playbackSpeed; // Apply playback speed
        v.play().then(() => {
          setIsPlaying(true);
          toast.success('Playing (fallback mode)');
        }).catch(() => {
          toast.error('Playback failed');
        });
      } else {
        toast.error('Playback failed');
      }
    }
  };

  // ===== TRACK CONTROLS =====
  
  // Save current state to undo stack before making changes
  const saveToUndoStack = () => {
    setUndoStack(prev => [...prev, tracks.map(t => ({ ...t }))]);
    setRedoStack([]); // Clear redo stack when new action is taken
  };
  
  // Undo last action
  const undo = () => {
    if (undoStack.length === 0) {
      toast.info('Nothing to undo');
      return;
    }
    
    // Save current state to redo stack
    setRedoStack(prev => [...prev, tracks.map(t => ({ ...t }))]);
    
    // Restore previous state
    const previousState = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setTracks(previousState);
    toast.success('Undo successful');
  };
  
  // Redo last undone action
  const redo = () => {
    if (redoStack.length === 0) {
      toast.info('Nothing to redo');
      return;
    }
    
    // Save current state to undo stack
    setUndoStack(prev => [...prev, tracks.map(t => ({ ...t }))]);
    
    // Restore next state
    const nextState = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setTracks(nextState);
    toast.success('Redo successful');
  };
  
  const deleteTrack = (idx) => {
    // Save to undo stack before deleting
    saveToUndoStack();
    
    URL.revokeObjectURL(tracks[idx].url);
    setTracks(prev => prev.filter((_, i) => i !== idx));
    toast.success('Track deleted (tap Undo to restore)');
  };
  
  // Download all tracks as individual files
  const downloadAllTracks = async () => {
    if (tracks.length === 0) {
      toast.error('No tracks to download');
      return;
    }
    
    toast.info(`Downloading ${tracks.length} track(s)...`);
    
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const link = document.createElement('a');
      link.href = track.url;
      
      // Determine file extension from blob type
      const ext = track.blob.type.includes('mp4') ? 'mp4' : 'webm';
      link.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_Track${i + 1}.${ext}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Small delay between downloads to prevent browser blocking
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast.success(`Downloaded ${tracks.length} track(s)`);
  };

  // Upload all tracks to personal cloud (DreamHost)
  const uploadToCloud = async (targetFolder = '') => {
    if (tracks.length === 0) {
      toast.error('No tracks to upload');
      return;
    }
    
    setIsUploadingToCloud(true);
    setCloudUploadProgress('Starting upload...');
    
    let uploaded = 0;
    let failed = 0;
    
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const ext = track.blob.type.includes('mp4') ? 'mp4' : 'webm';
      // Use track name if available, otherwise simple Track number
      const filename = track.name || `Track${i + 1}.${ext}`;
      
      setCloudUploadProgress(`Uploading track ${i + 1} of ${tracks.length}...`);
      
      try {
        const formData = new FormData();
        formData.append('file', track.blob, filename);
        if (targetFolder || selectedUploadFolder) {
          formData.append('folder', targetFolder || selectedUploadFolder);
        }
        
        const response = await fetch(`${API}/api/cloud/upload`, {
          method: 'POST',
          headers: authHeaders(),
          body: formData
        });
        
        const result = await response.json();
        
        if (result.success) {
          uploaded++;
        } else {
          failed++;
          console.error('Upload failed:', result.error);
        }
      } catch (err) {
        failed++;
        console.error('Upload error:', err);
      }
    }
    
    setIsUploadingToCloud(false);
    setCloudUploadProgress('');
    
    if (failed === 0) {
      toast.success(`Uploaded ${uploaded} track(s) to your cloud!`);
    } else if (uploaded > 0) {
      toast.warning(`Uploaded ${uploaded} tracks, ${failed} failed`);
    } else {
      toast.error('Upload failed. Check if cloud.casasamba.com is set up.');
    }
  };

  // Fetch list of files from cloud (with folder support)
  const fetchCloudFiles = async (folder = '') => {
    setIsLoadingCloudFiles(true);
    try {
      const folderParam = folder ? `?folder=${encodeURIComponent(folder)}` : '';
      const response = await fetch(`${API}/api/cloud/list${folderParam}`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (data.files !== undefined) {
        setCloudFiles(data.files);
        setCloudFolders(data.folders || []);
        setCurrentCloudFolder(data.current_folder || '');
        setShowCloudFiles(true);
      } else {
        toast.error('Could not fetch cloud files');
      }
    } catch (err) {
      console.error('Cloud fetch error:', err);
      toast.error('Could not connect to cloud');
    }
    setIsLoadingCloudFiles(false);
  };

  // Create new folder in cloud
  const createCloudFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Enter a folder name');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/cloud/folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ folder: newFolderName.trim() })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Folder "${newFolderName}" created!`);
        setNewFolderName('');
        setShowNewFolderInput(false);
        fetchCloudFiles(currentCloudFolder);
      } else {
        toast.error(data.error || 'Could not create folder');
      }
    } catch (err) {
      toast.error('Could not create folder');
    }
  };

  // Delete folder from cloud
  const deleteCloudFolder = async (folder) => {
    if (!confirm(`Delete folder "${folder}" and all its contents?`)) return;
    
    try {
      const response = await fetch(`${API}/api/cloud/folder`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ folder })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Folder deleted');
        fetchCloudFiles('');
      } else {
        toast.error(data.error || 'Could not delete folder');
      }
    } catch (err) {
      toast.error('Could not delete folder');
    }
  };

  // Download a file from cloud and add as track
  const downloadFromCloud = async (file) => {
    setIsDownloadingFromCloud(true);
    try {
      toast.info(`Downloading ${file.name}...`);
      
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Determine if it's audio or video
      const isVideo = ['webm', 'mp4', 'mov'].includes(file.type?.toLowerCase());
      
      const newTrack = {
        id: Date.now(),
        blob,
        url,
        volume: 100,
        muted: false,
        solo: false,
        name: file.name,
        isVideo: isVideo
      };
      
      // Save current state for undo
      setUndoStack(prev => [...prev, [...tracks]]);
      setRedoStack([]);
      
      setTracks(prev => [...prev, newTrack]);
      toast.success(`Added "${file.name}" as new track`);
      
    } catch (err) {
      console.error('Download error:', err);
      toast.error(`Failed to download ${file.name}`);
    }
    setIsDownloadingFromCloud(false);
  };

  // Delete file from cloud (with folder support)
  const deleteFromCloud = async (filename, folder = '') => {
    if (!confirm(`Delete "${filename}" from cloud?`)) return;
    
    try {
      const response = await fetch(`${API}/api/cloud/file`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ filename, folder })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('File deleted from cloud');
        fetchCloudFiles(currentCloudFolder); // Refresh list
      } else {
        toast.error(data.error || 'Delete failed');
      }
    } catch (err) {
      toast.error('Could not delete file');
    }
  };

  const toggleMute = (idx) => {
    setTracks(prev => prev.map((t, i) => {
      if (i === idx) {
        const newMuted = !t.muted;
        toast.info(`Track ${idx + 1} ${newMuted ? 'muted' : 'unmuted'}`);
        // Also update the video element
        const v = trackRefs.current[idx];
        if (v) v.muted = newMuted;
        return { ...t, muted: newMuted };
      }
      return t;
    }));
  };
  
  const toggleSolo = (idx) => {
    setTracks(prev => prev.map((t, i) => {
      if (i === idx) {
        const newSolo = !t.solo;
        toast.info(`Track ${idx + 1} solo ${newSolo ? 'ON' : 'OFF'}`);
        return { ...t, solo: newSolo };
      }
      return t;
    }));
  };
  
  const setVolume = (idx, vol) => {
    setTracks(prev => prev.map((t, i) => i === idx ? { ...t, volume: vol } : t));
    const v = trackRefs.current[idx]; 
    if (v) v.volume = vol / 100;
    
    // Update Web Audio gain node in real-time during playback
    const trackId = tracks[idx]?.id;
    if (trackId) {
      const activeSource = activeSourcesRef.current.find(s => s.trackId === trackId);
      if (activeSource && activeSource.gainNode) {
        activeSource.gainNode.gain.value = vol / 100;
      }
    }
  };

  const exportTracks = () => {
    tracks.forEach((t, i) => {
      const a = document.createElement('a'); a.href = t.url; a.download = `track-${i + 1}.webm`;
      setTimeout(() => a.click(), i * 500);
    });
  };

  // ===== LOGIN =====
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try { await login(loginEmail, loginPassword); } catch (err) { toast.error(err.message || 'Failed'); }
    setIsLoggingIn(false);
  };

  // ===== WAVEFORM COMPONENT =====
  const Waveform = ({ trackId, waveform, isActive }) => {
    if (!waveform || waveform.length === 0) {
      return (
        <div className="h-12 bg-stone-800 rounded-lg flex items-center justify-center">
          <span className="text-stone-500 text-xs">Loading waveform...</span>
        </div>
      );
    }
    
    const barWidth = 100 / waveform.length;
    
    return (
      <div className="relative h-12 bg-stone-800 rounded-lg overflow-hidden">
        {/* Waveform bars */}
        <div className="absolute inset-0 flex items-center gap-[1px] px-1">
          {Array.from(waveform).map((value, i) => {
            const height = Math.max(8, value * 100);
            const isBeforePlayhead = isActive && (i / waveform.length) < playbackPosition;
            return (
              <div
                key={i}
                className={`rounded-sm transition-colors duration-100 ${
                  isBeforePlayhead ? 'bg-amber-500' : 'bg-stone-600'
                }`}
                style={{
                  width: `${barWidth}%`,
                  height: `${height}%`,
                  minWidth: '2px'
                }}
              />
            );
          })}
        </div>
        
        {/* Playhead line */}
        {isActive && playbackPosition > 0 && (
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg shadow-white/50 z-10"
            style={{ left: `${playbackPosition * 100}%` }}
          />
        )}
        
        {/* Current time overlay */}
        {isActive && playbackPosition > 0 && currentTrackDuration > 0 && (
          <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-black/70 px-2 py-0.5 rounded text-xs font-bold text-amber-400">
            {Math.floor((playbackPosition * currentTrackDuration) / 60)}:{String(Math.floor((playbackPosition * currentTrackDuration) % 60)).padStart(2, '0')}
          </div>
        )}
      </div>
    );
  };

  if (authLoading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  // TEMPORARILY DISABLED FOR TESTING - Remove this comment block to re-enable login
  /*
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-900 to-black text-white p-6">
        <Link to="/" className="inline-block mb-6"><ArrowLeft className="w-6 h-6" /></Link>
        <div className="max-w-sm mx-auto">
          <div className="text-center mb-8">
            <Video className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold">
              <PageEditable path="login.title" as="span" fallback="Mobile Studio" />
            </h1>
            <p className="text-stone-400">
              <PageEditable path="login.tagline" as="span" fallback="Professional Multi-Track Recording" />
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
              placeholder="Email" className="w-full p-4 bg-stone-800 rounded-xl border border-stone-700"
              data-testid="mobile-studio-email-input" />
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)} placeholder="Password"
                className="w-full p-4 bg-stone-800 rounded-xl border border-stone-700 pr-12"
                data-testid="mobile-studio-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-4">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <button type="submit" disabled={isLoggingIn}
              className="w-full p-4 bg-amber-500 text-black font-bold rounded-xl"
              data-testid="mobile-studio-login-btn">{isLoggingIn ? 'Signing in...' : 'Sign In'}</button>
          </form>
        </div>
      </div>
    );
  }
  */

  // ===== MAIN STUDIO - SCROLLABLE =====
  
  // Fullscreen overlay
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[100] bg-black">
        {/* Full screen camera */}
        <div className="w-full h-full relative">
          <video ref={videoRef} autoPlay muted playsInline
            className={`w-full h-full object-cover ${bgRemovalEnabled ? 'hidden' : ''}`} />
          {bgRemovalEnabled && <canvas ref={canvasRef} className="w-full h-full object-cover" />}
          
          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
            <div className="flex gap-2">
              {bgRemovalEnabled && <span className="bg-purple-600 px-2 py-1 rounded text-xs font-bold">AI BG</span>}
              {metronomeOn && <span className="bg-green-600 px-2 py-1 rounded text-xs font-bold">♪ {bpm}</span>}
              {isRecording && <span className="bg-red-600 px-2 py-1 rounded text-xs font-bold animate-pulse">● REC</span>}
            </div>
            
            {/* Exit fullscreen */}
            <button onClick={toggleFullscreen} className="p-3 bg-black/60 rounded-full">
              <Minimize className="w-6 h-6" />
            </button>
          </div>
          
          {/* Audio level */}
          <div className="absolute top-16 left-4 right-4">
            <div className="h-2 bg-black/50 rounded-full overflow-hidden">
              <div className={`h-full ${audioLevel > 85 ? 'bg-red-500' : audioLevel > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                style={{ width: `${audioLevel}%` }} />
            </div>
          </div>
          
          {/* Bottom controls */}
          <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-center gap-6 bg-gradient-to-t from-black/70 to-transparent">
            {/* Flip camera */}
            <button 
              onClick={switchCamera}
              disabled={isSwitchingCamera || !hasCamera}
              className={`p-4 bg-black/60 rounded-full ${isSwitchingCamera ? 'opacity-50' : ''}`}>
              <RotateCcw className={`w-6 h-6 ${isSwitchingCamera ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Record */}
            <button onClick={isRecording ? stopRecording : startRecording}
              disabled={tracks.length >= 8}
              className={`w-20 h-20 rounded-full flex items-center justify-center ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-red-700'} disabled:opacity-50`}>
              {isRecording ? <Square className="w-8 h-8" /> : <Circle className="w-10 h-10 fill-white" />}
            </button>
            
            {/* Play/Stop */}
            <button onClick={() => isPlaying ? stopAllTracks() : playAllTracks()}
              disabled={!tracks.length}
              className={`p-4 rounded-full ${!tracks.length ? 'bg-black/60 opacity-50' : isPlaying ? 'bg-orange-600' : 'bg-green-600'}`}>
              {isPlaying ? <Square className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Track count */}
          <div className="absolute bottom-6 left-6 bg-black/60 px-3 py-1 rounded-full">
            <span className="text-sm font-bold">{tracks.length}/8 tracks</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-stone-950 text-white">
      
      {/* FIXED TOP: TRANSPORT */}
      <div className="sticky top-0 z-50 bg-stone-900 border-b border-stone-700">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2">
          <Link to="/"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="text-center">
            <h1 className="font-bold text-sm">
              <PageEditable path="header.title" as="span" fallback="Mobile Studio" />
            </h1>
            {/* Save status */}
            <div className="text-xs text-stone-400">
              {isSaving ? (
                <span className="text-amber-400">Saving...</span>
              ) : lastSaved ? (
                <span className="text-green-400">Auto-saved ✓</span>
              ) : tracks.length > 0 ? (
                <span className="text-stone-500">Not saved</span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Undo/Redo buttons */}
            <button 
              onClick={undo} 
              disabled={undoStack.length === 0}
              className="p-2 rounded-lg bg-stone-800 disabled:opacity-30"
              title="Undo"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button 
              onClick={redo} 
              disabled={redoStack.length === 0}
              className="p-2 rounded-lg bg-stone-800 disabled:opacity-30"
              title="Redo"
            >
              <Redo2 className="w-4 h-4" />
            </button>
            <span className="text-xs bg-stone-800 px-2 py-1 rounded">{tracks.length}/8</span>
            <button onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg ${showSettings ? 'bg-amber-500 text-black' : 'bg-stone-800'}`}>
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Quick Settings Panel */}
        {showSettings && (
          <div className="bg-stone-800 px-4 py-3 border-t border-stone-700 space-y-3">
            {/* Project Management - Simplified */}
            <div className="pb-3 border-b border-stone-700">
              <span className="text-sm font-bold text-amber-500 block mb-2">💾 SAVE YOUR WORK</span>
              
              {/* Primary action - Download to Device */}
              <button 
                onClick={downloadAllTracks}
                disabled={tracks.length === 0}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-stone-700 disabled:opacity-50 rounded-lg text-base font-bold mb-2"
              >
                📥 Save Tracks to My Device
              </button>
              <p className="text-xs text-stone-400 mb-3">
                Downloads to your Files app (iPhone) or Downloads folder. 
                You can then move them to DreamHost, iCloud, or anywhere you want.
              </p>
              
              {/* Project actions - Cloud is primary */}
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    if (tracks.length > 0 && !window.confirm('Start new project? Current tracks will be cleared.')) return;
                    setTracks([]);
                    clearSavedProject();
                    setUndoStack([]);
                    setRedoStack([]);
                    setProjectName('Untitled Project');
                    toast.success('New project started');
                  }}
                  className="px-4 py-3 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm font-bold"
                >
                  🆕 New
                </button>
                <button 
                  onClick={uploadToCloud}
                  disabled={tracks.length === 0 || isUploadingToCloud}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-stone-700 disabled:to-stone-700 disabled:opacity-50 rounded-lg text-base font-bold"
                >
                  {isUploadingToCloud ? cloudUploadProgress : '☁️ Upload to Cloud'}
                </button>
                <button 
                  onClick={fetchCloudFiles}
                  disabled={isLoadingCloudFiles}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-stone-700 disabled:to-stone-700 disabled:opacity-50 rounded-lg text-base font-bold"
                >
                  {isLoadingCloudFiles ? '⏳ Loading...' : '📥 Load from Cloud'}
                </button>
              </div>
              <p className="text-xs text-stone-400 mt-1 text-center">
                Your personal cloud: cloud.casasamba.com
              </p>
            </div>
            
            {/* Metronome */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Metronome</span>
              <button onClick={toggleMetronome}
                className={`px-3 py-1 rounded font-bold text-sm ${metronomeOn ? 'bg-green-600' : 'bg-stone-700'}`}>
                {metronomeOn ? 'ON' : 'OFF'}
              </button>
              <input type="range" min="60" max="200" value={bpm} onChange={e => setBpm(Number(e.target.value))} className="flex-1" />
              <span className="text-sm w-12">{bpm} BPM</span>
            </div>
            
            {/* Score Display */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Score</span>
              <button 
                onClick={() => setShowScoreDisplay(true)}
                className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 rounded-lg text-sm font-bold flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Open Sheet Music / Patterns
              </button>
            </div>
            
            {/* Pro Features */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Pro</span>
              <button 
                onClick={() => setShowProFeatures(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-sm font-bold flex items-center gap-2"
              >
                <Star className="w-4 h-4" />
                4K Export, Stems, Templates
              </button>
            </div>
            
            {/* Playback Speed (Slow Motion Practice) */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Speed</span>
              <div className="flex gap-1">
                {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                  <button 
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      playbackSpeed === speed 
                        ? speed === 1 ? 'bg-green-600' : 'bg-amber-600' 
                        : 'bg-stone-700'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-stone-500 -mt-2 ml-28">Slow down playback to practice difficult parts</p>
            
            {/* Camera */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Camera</span>
              <button 
                onClick={switchCamera}
                disabled={isSwitchingCamera || !hasCamera}
                className={`px-3 py-1 bg-stone-700 rounded text-sm flex items-center gap-1 ${isSwitchingCamera ? 'opacity-50' : ''}`}>
                <RotateCcw className={`w-4 h-4 ${isSwitchingCamera ? 'animate-spin' : ''}`} /> 
                {isSwitchingCamera ? 'Switching...' : (facingMode === 'user' ? 'Front' : 'Back')}
              </button>
            </div>
            
            {/* AI Background */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">AI Background</span>
              <button onClick={() => { if (!bgRemovalEnabled) { setBgRemovalEnabled(true); initBgRemoval(); } else setBgRemovalEnabled(false); }}
                disabled={isLoadingBg}
                className={`px-3 py-1 rounded font-bold text-sm ${bgRemovalEnabled ? 'bg-purple-600' : 'bg-stone-700'}`}>
                {isLoadingBg ? 'Loading...' : bgRemovalEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
            
            {/* Rhythm Guide */}
            <div className="flex items-center gap-3">
              <span className="text-sm w-24">Rhythm Guide</span>
              <button onClick={() => setRhythmGuideEnabled(!rhythmGuideEnabled)}
                className={`px-3 py-1 rounded font-bold text-sm flex items-center gap-1 ${rhythmGuideEnabled ? 'bg-amber-600' : 'bg-stone-700'}`}>
                <Activity className="w-4 h-4" /> {rhythmGuideEnabled ? 'ON' : 'OFF'}
              </button>
              <span className="text-xs text-stone-500">Visual beat cues</span>
            </div>
            
            {/* Audio Input Selection */}
            <div className="border-t border-stone-700 pt-3 mt-3">
              <p className="text-sm text-amber-400 font-bold mb-3">🎛️ EXTERNAL AUDIO</p>
              <p className="text-sm text-stone-300 mb-3">For Zoom PodTrak, USB mixers, headphone jack input</p>
              
              {/* Step 1: Connect Device */}
              <div className="bg-stone-800 rounded-lg p-3 mb-3">
                <p className="text-sm font-bold text-white mb-2">Step 1: Select Your Device</p>
                
                {/* Device Selector */}
                <select 
                  value={selectedAudioInput}
                  onChange={(e) => setSelectedAudioInput(e.target.value)}
                  className="w-full px-3 py-2 bg-stone-700 rounded text-sm font-medium mb-2"
                >
                  <option value="default">📱 Default iPhone Microphone</option>
                  {audioInputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      🎤 {device.label || `Audio Device ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
                
                {/* Scan Buttons */}
                <div className="flex gap-2 mb-2">
                  <button 
                    onClick={enumerateAudioDevices}
                    className="flex-1 px-3 py-2 bg-blue-600 rounded text-sm font-bold"
                  >
                    🔄 Quick Scan
                  </button>
                  <button 
                    onClick={forceRescanDevices}
                    className="flex-1 px-3 py-2 bg-amber-600 rounded text-sm font-bold"
                  >
                    🔍 Deep Scan
                  </button>
                </div>
                
                <p className="text-xs text-stone-400 mb-3">
                  {audioInputDevices.length === 0 
                    ? "Don't see your Zoom? Tap 'Deep Scan' after connecting it"
                    : `${audioInputDevices.length} device(s) found`
                  }
                </p>
                
                {/* Test Audio Button */}
                <button 
                  onClick={testAudioInput}
                  className="w-full px-3 py-2 bg-purple-600 rounded text-sm font-bold"
                >
                  🎵 Test Audio Input (5 sec)
                </button>
                <p className="text-xs text-stone-500 mt-1">
                  Tests if audio is coming through from selected device
                </p>
              </div>
              
              {/* Step 2: Enable Mixer Mode */}
              <div className="bg-stone-800 rounded-lg p-3 mb-3">
                <p className="text-sm font-bold text-white mb-2">Step 2: Enable Mixer Mode</p>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setMixerMode(!mixerMode)}
                    className={`px-4 py-2 rounded font-bold text-sm ${mixerMode ? 'bg-green-600' : 'bg-stone-600'}`}
                  >
                    {mixerMode ? '✓ ON' : 'OFF'}
                  </button>
                  <span className="text-sm text-stone-300">
                    {mixerMode ? 'Audio processing disabled' : 'Turn ON for external devices'}
                  </span>
                </div>
              </div>
              
              {/* Input Gain Control - Always visible when mixer mode is on */}
              {mixerMode && (
                <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-bold text-green-400 mb-2">🎚️ Input Volume</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-stone-400 w-8">0%</span>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={inputGain}
                      onChange={(e) => setInputGain(Number(e.target.value))}
                      className="flex-1 h-3 accent-green-500 cursor-pointer" 
                    />
                    <span className="text-sm font-bold text-white w-12 text-right">{inputGain}%</span>
                  </div>
                  <p className="text-xs text-stone-400 mt-2">
                    Lower this if your Zoom/mixer audio is too loud
                  </p>
                </div>
              )}
              
              {/* Step 3: iOS Info */}
              {mixerMode && (
                <div className="bg-amber-900/40 border border-amber-600/50 rounded-lg p-3 mb-3">
                  <p className="text-sm font-bold text-amber-400 mb-2">Step 3: iOS Settings</p>
                  <div className="text-sm text-white space-y-2">
                    <p>If Wide Spectrum is available:</p>
                    <p>1. <span className="text-cyan-400">Swipe down from top-right</span></p>
                    <p>2. Tap <span className="text-cyan-400">"Mic Mode"</span></p>
                    <p>3. Select <span className="text-green-400">"Wide Spectrum"</span> or <span className="text-green-400">"Standard"</span></p>
                  </div>
                  <p className="text-xs text-stone-400 mt-2">
                    Note: Wide Spectrum may be grayed out in Safari. Try "Standard" mode instead.
                  </p>
                </div>
              )}
              
              {/* Troubleshooting Tips */}
              <div className="bg-stone-800/50 rounded-lg p-3 mb-3">
                <p className="text-sm font-bold text-stone-300 mb-2">💡 Troubleshooting</p>
                <ul className="text-xs text-stone-400 space-y-1">
                  <li>• If Zoom doesn't appear, try unplugging and replugging it</li>
                  <li>• Make sure Zoom is set to "Audio Interface" mode</li>
                  <li>• Try using a Lightning-to-USB adapter (Camera Kit)</li>
                  <li>• Some devices work via headphone jack - check for that option</li>
                  <li>• Tap "Test Audio" to verify signal is coming through</li>
                </ul>
              </div>
              
              {/* Link to Audio Setup */}
              <Link 
                to="/audio-setup"
                className="block w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-center font-bold text-sm"
              >
                🔧 Open Audio Setup Tool
              </Link>
              <p className="text-xs text-stone-500 text-center mt-1">
                Dedicated tool to detect & configure external audio devices
              </p>
            </div>
          </div>
        )}
        
        {/* Audio Level */}
        <div className="px-4 py-2 flex items-center gap-2">
          <Mic className={`w-4 h-4 ${isRecording ? 'text-red-500 animate-pulse' : 'text-stone-500'}`} />
          <div className="flex-1 h-4 bg-stone-800 rounded-full overflow-hidden">
            <div className={`h-full ${audioLevel > 85 ? 'bg-red-500' : audioLevel > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${audioLevel}%` }} />
          </div>
          <span className="text-xs w-10">{Math.round(audioLevel)}%</span>
          {isRecording && <span className="text-xs text-red-500 font-bold">● REC</span>}
        </div>
        
        {/* Transport Buttons - All same size, equal spacing */}
        <div className="flex items-center justify-center gap-3 px-4 py-3">
          {/* Rewind */}
          <button onClick={() => {
            tracks.forEach((_, i) => { const v = trackRefs.current[i]; if (v) v.currentTime = 0; });
            toast.info('Rewound to start');
          }} disabled={!tracks.length} className="w-12 h-12 bg-stone-800 rounded-full disabled:opacity-50 flex items-center justify-center active:bg-stone-700">
            <SkipBack className="w-6 h-6" />
          </button>
          
          {/* Play */}
          <button onClick={playAllTracks} disabled={!tracks.length}
            className={`w-12 h-12 rounded-full flex items-center justify-center active:opacity-80 ${!tracks.length ? 'bg-stone-800 opacity-50' : 'bg-green-600'}`}>
            <Play className="w-6 h-6" />
          </button>
          
          {/* Stop - Also stops recording if in progress */}
          <button onClick={() => {
            if (isRecording) stopRecording();
            stopAllTracks();
          }} disabled={!tracks.length && !isRecording}
            className={`w-12 h-12 rounded-full flex items-center justify-center active:bg-orange-700 ${
              isRecording ? 'bg-orange-600 animate-pulse' : 'bg-orange-600'
            } disabled:opacity-50 disabled:bg-stone-800`}>
            <StopCircle className="w-6 h-6" />
          </button>
          
          {/* RECORD */}
          <button onClick={isRecording ? stopRecording : startRecording} disabled={!hasCamera || tracks.length >= 8}
            className={`w-12 h-12 rounded-full flex items-center justify-center ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-red-700'} disabled:opacity-50 disabled:bg-stone-800`}
            data-testid="mobile-studio-record-btn">
            {isRecording ? <Square className="w-6 h-6" /> : <Circle className="w-6 h-6 fill-white" />}
          </button>
          
          {/* Fast Forward */}
          <button onClick={() => {
            tracks.forEach((_, i) => { const v = trackRefs.current[i]; if (v && v.duration) v.currentTime = v.duration - 0.1; });
            toast.info('Skipped to end');
          }} disabled={!tracks.length} className="w-12 h-12 bg-stone-800 rounded-full disabled:opacity-50 flex items-center justify-center active:bg-stone-700">
            <SkipForward className="w-6 h-6" />
          </button>
          
          {/* Export/Download */}
          <button onClick={exportTracks} disabled={!tracks.length} className="w-12 h-12 bg-blue-600 rounded-full disabled:opacity-50 disabled:bg-stone-800 flex items-center justify-center active:bg-blue-700">
            <Download className="w-6 h-6" />
          </button>
          
          {/* Share */}
          <button onClick={() => handleNativeShare()} disabled={!tracks.length} className="w-12 h-12 bg-pink-600 rounded-full disabled:opacity-50 disabled:bg-stone-800 flex items-center justify-center active:bg-pink-700">
            <Share2 className="w-6 h-6" />
          </button>
          
          {/* Upload to Gallery */}
          <button onClick={() => setShowShareModal(true)} disabled={!tracks.length || !user} className="w-12 h-12 bg-purple-600 rounded-full disabled:opacity-50 disabled:bg-stone-800 flex items-center justify-center active:bg-purple-700">
            <Upload className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="p-4 space-y-6 pb-20">
        
        {/* ===== CAMERA PREVIEW - FULL HEIGHT ===== */}
        <section>
          <div 
            ref={cameraContainerRef}
            className={`relative bg-black rounded-xl overflow-hidden ${isFullscreen ? 'rounded-none' : ''}`} 
            style={{ height: isFullscreen ? '100vh' : '65vh' }}
          >
            <video ref={videoRef} autoPlay muted playsInline
              className={`w-full h-full object-cover ${bgRemovalEnabled ? 'hidden' : ''}`} />
            {bgRemovalEnabled && <canvas ref={canvasRef} className="w-full h-full object-cover" />}
            
            {/* Camera not connected overlay */}
            {!hasCamera && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-900 p-6 text-center">
                <Camera className="w-16 h-16 text-stone-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">
                  <PageEditable path="camera.notConnectedTitle" as="span" fallback="Camera Not Connected" />
                </h3>
                <p className="text-stone-400 text-sm mb-6">
                  Tap the button below to enable your camera and microphone.
                </p>
                <button 
                  onClick={initCamera}
                  className="px-6 py-3 bg-amber-500 text-black font-bold rounded-xl mb-4"
                >
                  Enable Camera
                </button>
                <div className="text-stone-500 text-xs space-y-1">
                  <p>If prompted, tap "Allow" for camera and microphone.</p>
                  <p className="mt-2">Still not working? Try:</p>
                  <p>• Refresh the page</p>
                  <p>• Check browser settings → Site Settings → Camera</p>
                  <p>• Use Chrome or Safari browser</p>
                </div>
              </div>
            )}
            
            {/* Badges */}
            <div className="absolute top-2 left-2 flex gap-2">
              {bgRemovalEnabled && <span className="bg-purple-600 px-2 py-1 rounded text-xs font-bold">AI BG</span>}
              {metronomeOn && <span className="bg-green-600 px-2 py-1 rounded text-xs font-bold">♪ {bpm}</span>}
              {isRecording && <span className="bg-red-600 px-2 py-1 rounded text-xs font-bold animate-pulse">● REC</span>}
            </div>
            
            {/* Bottom controls - only show when camera is connected */}
            {hasCamera && (
              <div className="absolute bottom-2 right-2 flex gap-2">
                {/* Camera flip */}
                <button 
                  onClick={switchCamera}
                  disabled={isSwitchingCamera}
                  className={`p-3 bg-black/60 rounded-full ${isSwitchingCamera ? 'opacity-50' : ''}`}>
                  <RotateCcw className={`w-6 h-6 ${isSwitchingCamera ? 'animate-spin' : ''}`} />
                </button>
                
                {/* Fullscreen toggle */}
                <button onClick={toggleFullscreen}
                  className="p-3 bg-black/60 rounded-full">
                  {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
                </button>
              </div>
            )}
            
            {/* Fullscreen record button */}
            {isFullscreen && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
                <button onClick={isRecording ? stopRecording : startRecording}
                  className={`w-16 h-16 rounded-full flex items-center justify-center ${isRecording ? 'bg-red-600 animate-pulse' : 'bg-red-700'}`}>
                  {isRecording ? <Square className="w-8 h-8" /> : <Circle className="w-10 h-10 fill-white" />}
                </button>
              </div>
            )}
          </div>
          
          {/* Scroll indicator - hide in fullscreen */}
          {!isFullscreen && (
            <div className="flex flex-col items-center mt-4 animate-bounce">
              <span className="text-stone-400 text-xs mb-1">Scroll for more</span>
              <ChevronDown className="w-6 h-6 text-stone-400" />
            </div>
          )}
        </section>

        {/* ===== TRACKS SECTION ===== */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Music className="w-5 h-5 text-amber-500" />
              Tracks ({tracks.length}/8)
            </h2>
            
            {/* View Toggle */}
            {tracks.length > 0 && (
              <div className="flex items-center gap-1 bg-stone-800 rounded-lg p-1">
                <button 
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-amber-500 text-black' : 'text-stone-400'}`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-amber-500 text-black' : 'text-stone-400'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          {tracks.length === 0 ? (
            <div className="bg-stone-900 rounded-xl p-6 text-center text-stone-500">
              No tracks yet. Press the red record button above.
            </div>
          ) : viewMode === 'grid' ? (
            /* ===== GRID VIEW - All tracks visible at once ===== */
            <div className="bg-stone-900 rounded-xl p-3">
              
              {/* FOCUSED VIEW - Single track expanded */}
              {focusedTrackIdx !== null && tracks[focusedTrackIdx] ? (
                <div>
                  {/* Back to grid button */}
                  <button 
                    onClick={() => setFocusedTrackIdx(null)}
                    className="mb-3 px-4 py-2 bg-stone-700 rounded-lg font-bold flex items-center gap-2 text-sm"
                  >
                    <Grid className="w-4 h-4" /> Back to Grid
                  </button>
                  
                  {/* Expanded single track */}
                  <div className="relative">
                    <video 
                      ref={el => { trackRefs.current[focusedTrackIdx] = el; }} 
                      src={tracks[focusedTrackIdx].url} 
                      className="w-full aspect-video object-contain rounded-lg bg-black"
                      playsInline 
                      preload="auto"
                      onClick={() => playTrack(focusedTrackIdx)}
                    />
                    {/* Track number badge */}
                    <div className="absolute top-2 left-2 px-3 py-1 bg-amber-600 rounded-lg font-bold">
                      Track {focusedTrackIdx + 1}
                    </div>
                    {tracks[focusedTrackIdx].muted && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                        <VolumeX className="w-10 h-10 text-red-500" />
                        <span className="ml-2 text-red-500 font-bold text-xl">MUTED</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Focused track controls */}
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <button 
                      onClick={() => playTrack(focusedTrackIdx)}
                      className="px-6 py-2 bg-green-600 rounded-lg font-bold flex items-center gap-2"
                    >
                      <Play className="w-5 h-5" /> Play
                    </button>
                    <button 
                      onClick={stopAllTracks}
                      className="px-6 py-2 bg-orange-600 rounded-lg font-bold flex items-center gap-2"
                    >
                      <Square className="w-5 h-5" /> Stop
                    </button>
                    <button 
                      onClick={() => toggleMute(focusedTrackIdx)}
                      className={`px-4 py-2 rounded-lg font-bold ${tracks[focusedTrackIdx].muted ? 'bg-red-600' : 'bg-stone-700'}`}
                    >
                      {tracks[focusedTrackIdx].muted ? 'Unmute' : 'Mute'}
                    </button>
                  </div>
                  
                  {/* Quick track switcher */}
                  <div className="mt-4 pt-3 border-t border-stone-700">
                    <p className="text-xs text-stone-400 text-center mb-2">Switch to another track:</p>
                    <div className="flex justify-center gap-2">
                      {tracks.map((_, idx) => (
                        <button 
                          key={idx}
                          onClick={() => setFocusedTrackIdx(idx)}
                          className={`w-10 h-10 rounded-lg font-bold ${
                            idx === focusedTrackIdx ? 'bg-amber-500 text-black' : 'bg-stone-700'
                          }`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* GRID VIEW - All tracks */
                <>
                  {/* Grid of videos */}
                  <div className={`grid gap-2 ${
                    tracks.length === 1 ? 'grid-cols-1' : 
                    tracks.length === 2 ? 'grid-cols-2' : 
                    'grid-cols-2'
                  }`}>
                    {tracks.map((track, idx) => (
                      <div key={track.id} className="relative">
                        <video 
                          ref={el => { trackRefs.current[idx] = el; }} 
                          src={track.url} 
                          className={`w-full object-contain rounded-lg bg-black cursor-pointer ${
                            tracks.length <= 2 ? 'aspect-video' : 'aspect-square'
                          }`}
                          playsInline 
                          preload="auto"
                          muted
                          loop={false}
                          onLoadedData={(e) => {
                            // Ensure video is ready for playback
                            e.target.currentTime = 0;
                          }}
                          onClick={() => setFocusedTrackIdx(idx)}
                        />
                        {/* Track number badge */}
                        <div className="absolute top-1 left-1 w-6 h-6 bg-amber-600 rounded flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        {/* Expand hint */}
                        <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-[10px]">
                          Tap to expand
                        </div>
                        {/* Muted overlay */}
                        {track.muted && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                            <VolumeX className="w-6 h-6 text-red-500" />
                          </div>
                        )}
                        {/* Solo indicator */}
                        {track.solo && (
                          <div className="absolute top-1 right-1 bg-yellow-500 text-black px-1 rounded text-xs font-bold">
                            S
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grid controls */}
                  <div className="mt-3 pt-3 border-t border-stone-700">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <button 
                        onClick={playAllTracks}
                        disabled={!tracks.length}
                        className="px-6 py-2 bg-green-600 rounded-lg font-bold flex items-center gap-2 active:bg-green-700"
                      >
                        <Play className="w-5 h-5" /> Play All
                      </button>
                      <button 
                        onClick={stopAllTracks}
                        className="px-6 py-2 bg-orange-600 rounded-lg font-bold flex items-center gap-2 active:bg-orange-700"
                      >
                        <Square className="w-5 h-5" /> Stop
                      </button>
                    </div>
                    
                    {/* ===== MIXER SECTION - Scrollable ===== */}
                    <div className="bg-stone-800 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 mb-3">
                        <Sliders className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-bold text-blue-400">Mixer</span>
                        <span className="text-xs text-stone-500 ml-auto">← Scroll →</span>
                      </div>
                      
                      {/* Scrollable mixer container - prevent vertical scroll */}
                      <div 
                        className="overflow-x-auto pb-2"
                        style={{ touchAction: 'pan-x' }}
                      >
                        <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                          {tracks.map((track, idx) => (
                            <div key={track.id} className="flex flex-col items-center gap-1" style={{ minWidth: '60px' }}>
                              {/* Track label */}
                              <span className="text-[10px] font-bold text-stone-300">TRK {idx + 1}</span>
                              
                              {/* Fader + Peak meter row */}
                              <div className="flex gap-1">
                                {/* Volume fader - prevent page scroll */}
                                <div 
                                  className="relative h-32 w-6 bg-stone-900 rounded flex items-end justify-center overflow-hidden"
                                  style={{ touchAction: 'none' }}
                                >
                                  {/* Volume level indicator */}
                                  <div 
                                    className={`absolute bottom-0 left-0 right-0 transition-all duration-100 ${
                                      track.muted ? 'bg-red-600/50' : 'bg-green-500/80'
                                    }`}
                                    style={{ height: `${track.volume}%` }}
                                  />
                                  {/* Slider input (vertical) - with touch handling */}
                                  <input 
                                    type="range" 
                                    min="0" 
                                    max="100" 
                                    value={track.volume}
                                    onChange={(e) => setVolume(idx, Number(e.target.value))}
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onTouchMove={(e) => e.stopPropagation()}
                                    className="absolute w-32 h-6 cursor-pointer opacity-0"
                                    style={{ 
                                      transform: 'rotate(-90deg)',
                                      transformOrigin: 'center center',
                                      top: '50%',
                                      left: '50%',
                                      marginTop: '-12px',
                                      marginLeft: '-64px',
                                      touchAction: 'none'
                                    }}
                                  />
                                  {/* Fader handle */}
                                  <div 
                                    className="absolute left-0 right-0 h-3 bg-white rounded shadow-lg pointer-events-none"
                                    style={{ bottom: `calc(${track.volume}% - 6px)` }}
                                  />
                                </div>
                                
                                {/* Peak meter - always visible */}
                                <div className="relative h-32 w-3 bg-black rounded overflow-hidden border border-stone-700">
                                  {/* Static gradient background showing meter scale */}
                                  <div className="absolute inset-0 flex flex-col">
                                    <div className="h-[15%] bg-red-900/30" />
                                    <div className="h-[25%] bg-yellow-900/30" />
                                    <div className="flex-1 bg-green-900/30" />
                                  </div>
                                  
                                  {/* Peak level - animated */}
                                  <div 
                                    className={`absolute bottom-0 left-0 right-0 transition-all duration-75 ${
                                      (peakLevels[track.id] || 0) > 85 ? 'bg-red-500' : 
                                      (peakLevels[track.id] || 0) > 60 ? 'bg-yellow-400' : 'bg-green-400'
                                    }`}
                                    style={{ height: `${peakLevels[track.id] || 0}%` }}
                                  />
                                  
                                  {/* Meter segment lines */}
                                  <div className="absolute inset-0 flex flex-col justify-between py-0.5 pointer-events-none">
                                    {[...Array(12)].map((_, i) => (
                                      <div key={i} className="h-px bg-stone-600/50" />
                                    ))}
                                  </div>
                                  
                                  {/* Peak hold indicator (top red line when clipping) */}
                                  {(peakLevels[track.id] || 0) > 90 && (
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 animate-pulse" />
                                  )}
                                </div>
                              </div>
                              
                              {/* Volume percentage */}
                              <span className={`text-[10px] font-bold ${track.muted ? 'text-red-500' : 'text-green-400'}`}>
                                {track.volume}
                              </span>
                              
                              {/* Mute/Solo buttons */}
                              <div className="flex gap-0.5">
                                <button 
                                  onClick={() => toggleMute(idx)}
                                  className={`w-7 h-7 rounded text-[10px] font-bold ${track.muted ? 'bg-red-600' : 'bg-stone-700'}`}
                                >
                                  M
                                </button>
                                <button 
                                  onClick={() => toggleSolo(idx)}
                                  className={`w-7 h-7 rounded text-[10px] font-bold ${track.solo ? 'bg-yellow-500 text-black' : 'bg-stone-700'}`}
                                >
                                  S
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Peak meter legend */}
                      <div className="flex items-center justify-center gap-3 mt-2 text-[9px] text-stone-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-400 rounded-sm"></span> Normal</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-sm"></span> Loud</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm"></span> Peak</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Playback indicator */}
                  {isPlaying && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-green-400">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-bold">Playing...</span>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            /* ===== LIST VIEW - Detailed track cards ===== */
            <div className="space-y-3">
              {tracks.map((track, idx) => (
                <div key={track.id} className="bg-stone-900 rounded-xl p-4">
                  {/* Video Preview - Full view, not cropped */}
                  <div className="mb-3 relative">
                    <video 
                      ref={el => { trackRefs.current[idx] = el; }} 
                      src={track.url} 
                      className="w-full aspect-video object-contain rounded-lg bg-black"
                      playsInline 
                      preload="auto"
                      onClick={() => playTrack(idx)}
                    />
                    {track.muted && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                        <VolumeX className="w-8 h-8 text-red-500" />
                        <span className="ml-2 text-red-500 font-bold">MUTED</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs font-bold">
                      Tap to play
                    </div>
                  </div>
                  
                  {/* Audio Waveform - clearly below video */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-xs text-stone-400 font-medium">Audio Waveform</span>
                      {audioBuffersRef.current[track.id] && (
                        <span className="text-xs text-stone-500 ml-auto">
                          {Math.floor(audioBuffersRef.current[track.id].duration / 60)}:{String(Math.floor(audioBuffersRef.current[track.id].duration % 60)).padStart(2, '0')}
                        </span>
                      )}
                    </div>
                    <Waveform 
                      trackId={track.id} 
                      waveform={waveformData[track.id]} 
                      isActive={isPlaying && activeSourcesRef.current.some(s => s.trackId === track.id)}
                    />
                  </div>
                  
                  {/* Track Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center font-bold text-lg">{idx + 1}</span>
                    <span className="flex-1 font-bold text-lg">{track.name}</span>
                    <button onClick={() => deleteTrack(idx)}
                      className="px-4 py-2 bg-red-600 rounded-lg font-bold flex items-center gap-2">
                      <Trash2 className="w-5 h-5" /> DELETE
                    </button>
                  </div>
                  
                  {/* Track Controls */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button 
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); playTrack(idx); }} 
                      className="px-4 py-2 bg-green-600 rounded-lg font-bold flex items-center gap-1 active:bg-green-700">
                      <Play className="w-4 h-4" /> Play
                    </button>
                    <button 
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMute(idx); }}
                      className={`px-4 py-2 rounded-lg font-bold active:opacity-80 ${track.muted ? 'bg-red-600' : 'bg-stone-700'}`}>
                      {track.muted ? 'MUTED' : 'Mute'}
                    </button>
                    <button 
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSolo(idx); }}
                      className={`px-4 py-2 rounded-lg font-bold active:opacity-80 ${track.solo ? 'bg-yellow-500 text-black' : 'bg-stone-700'}`}>
                      {track.solo ? 'SOLO' : 'Solo'}
                    </button>
                  </div>
                  
                  {/* Volume */}
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-5 h-5 text-stone-400" />
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={track.volume}
                      onChange={(e) => { e.stopPropagation(); setVolume(idx, Number(e.target.value)); }}
                      className="flex-1 h-3 accent-green-500 cursor-pointer" 
                    />
                    <span className="w-12 text-right font-bold">{track.volume}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ===== METRONOME - Collapsible ===== */}
        <section>
          <button 
            onClick={() => setShowMetronome(!showMetronome)}
            className="w-full flex items-center justify-between bg-stone-900 rounded-xl p-4"
          >
            <div className="flex items-center gap-2">
              <Music className="w-5 h-5 text-green-500" />
              <span className="font-bold">Metronome</span>
              {metronomeOn && <span className="text-xs bg-green-600 px-2 py-0.5 rounded">ON - {bpm} BPM</span>}
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showMetronome ? 'rotate-180' : ''}`} />
          </button>
          
          {showMetronome && (
            <div className="bg-stone-800 rounded-b-xl p-4 -mt-2 flex items-center gap-4">
              <button onClick={toggleMetronome}
                className={`px-6 py-3 rounded-lg font-bold ${metronomeOn ? 'bg-green-600' : 'bg-stone-700'}`}>
                {metronomeOn ? 'ON' : 'OFF'}
              </button>
              <input type="range" min="60" max="200" value={bpm} onChange={e => setBpm(Number(e.target.value))}
                className="flex-1 h-2 accent-green-500" />
              <span className="text-xl font-bold w-16 text-center">{bpm}</span>
              <span className="text-stone-400">BPM</span>
            </div>
          )}
        </section>

        {/* ===== AI BACKGROUNDS - Collapsible ===== */}
        <section>
          <button 
            onClick={() => setShowAiBackground(!showAiBackground)}
            className="w-full flex items-center justify-between bg-stone-900 rounded-xl p-4"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <span className="font-bold">AI Background</span>
              {bgRemovalEnabled && <span className="text-xs bg-purple-600 px-2 py-0.5 rounded">ON</span>}
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showAiBackground ? 'rotate-180' : ''}`} />
          </button>
          
          {showAiBackground && (
            <div className="bg-stone-800 rounded-b-xl p-4 -mt-2 space-y-4">
              {/* Toggle */}
              <div className="flex items-center gap-4">
                <button onClick={() => { if (!bgRemovalEnabled) { setBgRemovalEnabled(true); initBgRemoval(); } else setBgRemovalEnabled(false); }}
                  disabled={isLoadingBg}
                  className={`px-6 py-3 rounded-lg font-bold ${bgRemovalEnabled ? 'bg-purple-600' : 'bg-stone-700'}`}>
                  {isLoadingBg ? 'Loading...' : bgRemovalEnabled ? 'AI ON' : 'AI OFF'}
                </button>
                {customBgUrl && (
                  <button onClick={() => { bgImageRef.current = null; setCustomBgUrl(null); }}
                    className="px-4 py-2 bg-stone-700 rounded-lg text-sm">Clear Custom</button>
                )}
              </div>
              
              {bgRemovalEnabled && (
                <>
                  {/* Solid Colors */}
                  <div>
                    <h3 className="text-sm text-stone-400 mb-2">
                      <PageEditable path="backgrounds.solidTitle" as="span" fallback="Solid Colors" />
                    </h3>
                    <div className="flex gap-2">
                      {COLOR_BACKGROUNDS.map(bg => (
                        <button key={bg.id} onClick={() => { setSelectedColorBg(bg); bgImageRef.current = null; setCustomBgUrl(null); }}
                          className={`w-12 h-12 rounded-lg border-2 ${selectedColorBg.id === bg.id && !customBgUrl ? 'border-white' : 'border-stone-600'}`}
                          style={{ backgroundColor: bg.color }} />
                      ))}
                    </div>
                  </div>
                  
                  {/* AI Generated */}
                  <div>
                    <h3 className="text-sm text-stone-400 mb-2">
                      <PageEditable path="backgrounds.aiTitle" as="span" fallback="AI Generated (tap to create)" />
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {AI_BACKGROUNDS.map(preset => (
                        <button key={preset.id} onClick={() => generateAiBackground(preset)}
                          disabled={isGeneratingBg}
                          className="p-3 bg-stone-700 rounded-lg text-sm font-medium text-left disabled:opacity-50">
                          {preset.name}
                        </button>
                      ))}
                    </div>
                    {isGeneratingBg && <p className="text-amber-400 text-sm mt-2 animate-pulse">Generating image...</p>}
                  </div>
                  
                  {/* Current BG preview */}
                  {customBgUrl && (
                    <div className="flex items-center gap-3">
                      <img loading="lazy" decoding="async" src={customBgUrl} alt="BG" className="w-20 h-12 object-cover rounded-lg" />
                      <span className="text-green-400 text-sm">Custom background active</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>

        {/* ===== EFFECTS PANEL - Collapsible ===== */}
        <section>
          <button 
            onClick={() => setShowEffects(!showEffects)}
            className="w-full flex items-center justify-between bg-stone-900 rounded-xl p-4"
          >
            <div className="flex items-center gap-2">
              <Sliders className="w-5 h-5 text-blue-500" />
              <span className="font-bold">Effects</span>
              {(effects.reverb > 0 || effects.delay > 0 || effects.bass !== 50 || effects.treble !== 50 || effects.compression > 0) && (
                <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">ACTIVE</span>
              )}
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showEffects ? 'rotate-180' : ''}`} />
          </button>
          
          {showEffects && (
            <div className="bg-stone-800 rounded-b-xl p-4 -mt-2 space-y-4">
              {/* Bass */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Bass</span>
                  <span className="text-sm text-stone-400">{effects.bass > 50 ? '+' : ''}{effects.bass - 50}</span>
                </div>
                <input 
                  type="range" min="0" max="100" value={effects.bass}
                  onChange={(e) => setEffects(prev => ({ ...prev, bass: Number(e.target.value) }))}
                  className="w-full h-2 accent-blue-500" 
                />
              </div>
              
              {/* Treble */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Treble</span>
                  <span className="text-sm text-stone-400">{effects.treble > 50 ? '+' : ''}{effects.treble - 50}</span>
                </div>
                <input 
                  type="range" min="0" max="100" value={effects.treble}
                  onChange={(e) => setEffects(prev => ({ ...prev, treble: Number(e.target.value) }))}
                  className="w-full h-2 accent-blue-500" 
                />
              </div>
              
              {/* Delay/Echo */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Delay/Echo</span>
                  <span className="text-sm text-stone-400">{effects.delay}%</span>
                </div>
                <input 
                  type="range" min="0" max="100" value={effects.delay}
                  onChange={(e) => setEffects(prev => ({ ...prev, delay: Number(e.target.value) }))}
                  className="w-full h-2 accent-blue-500" 
                />
              </div>
              
              {/* Compression */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Compression</span>
                  <span className="text-sm text-stone-400">{effects.compression}%</span>
                </div>
                <input 
                  type="range" min="0" max="100" value={effects.compression}
                  onChange={(e) => setEffects(prev => ({ ...prev, compression: Number(e.target.value) }))}
                  className="w-full h-2 accent-blue-500" 
                />
              </div>
              
              {/* Reset button */}
              <button 
                onClick={() => setEffects({ reverb: 0, delay: 0, bass: 50, treble: 50, compression: 0 })}
                className="w-full py-2 bg-stone-700 rounded-lg text-sm font-medium hover:bg-stone-600"
              >
                Reset All Effects
              </button>
              
              <p className="text-xs text-stone-500 text-center">
                Effects apply in real-time during playback
              </p>
            </div>
          )}
        </section>

        {/* ===== SIGN OUT ===== */}
        <section className="pt-6 border-t border-stone-800">
          <div className="flex items-center justify-between">
            <span className="text-stone-500 text-sm">{user?.email}</span>
            <button onClick={() => { logout(); navigate('/'); }}
              className="px-4 py-2 bg-stone-800 text-stone-400 rounded-lg text-sm flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </section>
        
        {/* ===== GALLERY LINK ===== */}
        <section className="mt-4">
          <Link 
            to="/studio-gallery"
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 font-bold"
          >
            <Users className="w-5 h-5" />
            Browse Community Gallery
          </Link>
        </section>
        
      </div>
      
      {/* ===== RHYTHM GUIDE OVERLAY ===== */}
      {rhythmGuideEnabled && isPlaying && (
        <div 
          className={`fixed top-0 left-0 right-0 h-2 z-50 transition-all duration-75 ${
            beatFlash ? 'bg-amber-500' : 'bg-transparent'
          }`}
        />
      )}
      
      {/* Beat indicator in corner */}
      {rhythmGuideEnabled && isPlaying && (
        <div className={`fixed top-20 right-4 w-12 h-12 rounded-full z-50 flex items-center justify-center transition-all duration-75 ${
          beatFlash ? 'bg-amber-500 scale-110' : 'bg-stone-800/80 scale-100'
        }`}>
          <Activity className={`w-6 h-6 ${beatFlash ? 'text-black' : 'text-amber-500'}`} />
        </div>
      )}
      
      {/* ===== SHARE TO GALLERY MODAL ===== */}
      {/* Cloud Files Modal */}
      {showCloudFiles && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-stone-900 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-stone-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  ☁️ {currentCloudFolder ? `📁 ${currentCloudFolder}` : 'Your Cloud Files'}
                </h2>
                <button 
                  onClick={() => setShowCloudFiles(false)}
                  className="p-2 hover:bg-stone-700 rounded-lg"
                >
                  ✕
                </button>
              </div>
              {/* Breadcrumb / Back button */}
              {currentCloudFolder && (
                <button
                  onClick={() => fetchCloudFiles('')}
                  className="mt-2 text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                >
                  ← Back to root
                </button>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {/* New Folder Input */}
              {showNewFolderInput && (
                <div className="bg-stone-800 rounded-lg p-3 mb-3 flex gap-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name..."
                    className="flex-1 bg-stone-700 rounded px-3 py-2 text-sm outline-none"
                    autoFocus
                  />
                  <button
                    onClick={createCloudFolder}
                    className="px-3 py-2 bg-green-600 hover:bg-green-500 rounded text-sm font-bold"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                    className="px-3 py-2 bg-stone-600 hover:bg-stone-500 rounded text-sm font-bold"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Folders */}
              {cloudFolders.length > 0 && (
                <div className="space-y-2 mb-4">
                  {cloudFolders.map((folder, idx) => (
                    <div key={idx} className="bg-amber-900/30 rounded-lg p-3 flex items-center gap-3">
                      <span className="text-2xl">📁</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-amber-300">{folder.name}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchCloudFiles(folder.name)}
                          className="px-3 py-2 bg-amber-600 hover:bg-amber-500 rounded text-sm font-bold"
                        >
                          Open
                        </button>
                        <button
                          onClick={() => deleteCloudFolder(folder.name)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded text-sm font-bold"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Files */}
              {cloudFiles.length === 0 && cloudFolders.length === 0 ? (
                <div className="text-center py-10 text-stone-500">
                  <p className="text-4xl mb-3">📁</p>
                  <p>No files in {currentCloudFolder ? `"${currentCloudFolder}"` : 'your cloud'} yet</p>
                  <p className="text-sm mt-1">Upload tracks or create a folder</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cloudFiles.map((file, idx) => (
                    <div key={idx} className="bg-stone-800 rounded-lg p-3 flex items-center gap-3">
                      <span className="text-2xl">{file.icon || '📄'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{file.name}</p>
                        <p className="text-xs text-stone-500">{file.size_formatted} • {file.date}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => downloadFromCloud(file)}
                          disabled={isDownloadingFromCloud}
                          className="px-3 py-2 bg-cyan-600 hover:bg-cyan-500 rounded text-sm font-bold disabled:opacity-50"
                        >
                          + Add
                        </button>
                        <button
                          onClick={() => deleteFromCloud(file.name, file.folder)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded text-sm font-bold"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-stone-700 space-y-2">
              {/* Folder selector for uploads */}
              <div className="flex gap-2">
                <select
                  value={selectedUploadFolder}
                  onChange={(e) => setSelectedUploadFolder(e.target.value)}
                  className="flex-1 bg-stone-800 rounded-lg px-3 py-2 text-sm outline-none"
                >
                  <option value="">Upload to: Root</option>
                  {cloudFolders.map((f, i) => (
                    <option key={i} value={f.name}>Upload to: 📁 {f.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setShowNewFolderInput(true)}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg font-bold text-sm"
                >
                  + Folder
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchCloudFiles(currentCloudFolder)}
                  disabled={isLoadingCloudFiles}
                  className="flex-1 py-3 bg-stone-700 hover:bg-stone-600 rounded-lg font-bold"
                >
                  {isLoadingCloudFiles ? '⏳ Refreshing...' : '🔄 Refresh'}
                </button>
                <a
                  href="https://cloud.casasamba.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-bold text-center"
                >
                  🌐 Open Cloud
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-stone-900 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-500" />
              Share to Gallery
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input 
                  type="text"
                  value={shareTitle}
                  onChange={(e) => setShareTitle(e.target.value)}
                  placeholder="My awesome recording"
                  className="w-full px-4 py-3 bg-stone-800 rounded-lg border border-stone-700 focus:border-amber-500 outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea 
                  value={shareDescription}
                  onChange={(e) => setShareDescription(e.target.value)}
                  placeholder="Tell us about your recording..."
                  rows={3}
                  className="w-full px-4 py-3 bg-stone-800 rounded-lg border border-stone-700 focus:border-amber-500 outline-none resize-none"
                />
              </div>
              
              <div className="bg-stone-800 rounded-lg p-3 text-sm text-stone-400">
                <p>📹 {tracks.length} track(s) will be shared</p>
                <p>👤 Posted as: {user?.name || user?.email?.split('@')[0]}</p>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="flex-1 py-3 bg-stone-700 rounded-lg font-bold"
                >
                  Cancel
                </button>
                <button 
                  onClick={shareToGallery}
                  disabled={isUploading || !shareTitle.trim()}
                  className="flex-1 py-3 bg-amber-500 text-black rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Share
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score Display Modal */}
      <ScoreDisplay 
        isOpen={showScoreDisplay} 
        onClose={() => setShowScoreDisplay(false)}
        currentBpm={bpm}
      />

      {/* Pro Features Modal */}
      <ProFeatures
        isOpen={showProFeatures}
        onClose={() => setShowProFeatures(false)}
        tracks={tracks}
        projectName={projectName}
        onApplyTemplate={(template) => {
          // Apply template: set BPM and create empty named tracks
          setBpm(template.bpm);
          setProjectName(template.name);
          toast.info(`Template applied: ${template.tracks.length} track slots ready`);
        }}
      />
    </div>
  );
}
